import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { UserService } from 'src/app/shared/services/user.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { JoinMeetingDialogComponent } from '../join-meeting-dialog/join-meeting-dialog.component';
import { StartMeetingDialogComponent } from '../start-meeting-dialog/start-meeting-dialog.component';
import { AdminMeetingDialogComponent } from '../admin-meeting-dialog/admin-meeting-dialog.component';
import { WaitingAttendanceDialogComponent } from '../waiting-attendance-dialog/waiting-attendance-dialog.component';
import { MinimizedMeetingService } from '../services/minimized-meeting.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { MessageDialogComponent } from 'src/app/shared/components/message-dialog/message-dialog.component';
import { Attachment, MeetingAttachment } from 'src/app/shared/models/app.models';
import { ViewAgendaItemDialogComponent } from 'src/app/core/agenda-items/view-agenda-item-dialog/view-agenda-item-dialog.component';
import { CreateAgendaItemDialogComponent } from 'src/app/core/agenda-items/create-agenda-item-dialog/create-agenda-item-dialog.component';

// Type definitions
export interface AgendaItem {
  subject: string;
  dateTime?: string;
  attachments?: Attachment[];
  department?: string;
  currentStage?: string;
  tcDecisionAR?: string;
  tcDecisionEN?: string;
  prIdentifiers?: string[];
  [key: string]: any;
}

export type BadgeColor = 'blue' | 'green' | 'red' | 'yellow' | 'gray' | 'purple' | 'orange';

export interface Member {
  name: string;
  role?: string;
  initials?: string;
  status?: string;
  [key: string]: any;
}

export interface AttendanceTableMember {
  number: number;
  memberNameArabic: string;
  memberNameEnglish: string;
  roleArabic: string;
  roleEnglish: string;
  attendance: boolean;
}

export interface MeetingItem {
  itemNumber: string;
  company?: string;
  [key: string]: any;
}

export interface MeetingDetails {
  meetingId: string;
  title: string;
  dateTime: string;
  duration?: string;
  location?: string;
  status?: string;
  createdBy?: string;
  createdDate?: string;
  members?: Member[];
  agendaItems?: AgendaItem[];
  [key: string]: any;
}

@Component({
  selector: 'app-view-meeting',
  templateUrl: './view-meeting.component.html',
  styleUrls: ['./view-meeting.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewMeetingComponent implements OnInit, OnDestroy {
  AppConstants = AppConstants;
  readonly demoMode = environment.DISABLE_BACKEND;
  meetingId: string = '';
  meetingDetails: any = null;
  
  // Missing properties
  meetingSessionDialogRef: MatDialogRef<any> | null = null;
  currentAttachments: MeetingAttachment[] = [];
  currentAgendaItemSubject: string = '';
  showAttachmentsDialog: boolean = false;
  currentAgendaIndex: number = 0; // Track current agenda index for syncing with hoster
  meetingAttendees: Array<{ userId: string; userName: string }> = []; // Track meeting attendees
  attendanceTableData: AttendanceTableMember[] = []; // Attendance table data
  agendaItemsWithDecisions: any[] = []; // Agenda items with decisions and voting status
  meetingAttachments: MeetingAttachment[] = []; // Meeting-level attachments
  isLoadingMeetingAttachments: boolean = false;
  showMeetingDocumentsDialog: boolean = false; // Dialog for meeting documents
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private cdr: ChangeDetectorRef;
  private isProcessingMeetingSession = false; // Flag to prevent multiple rapid opens
  isDialogOpen: boolean = false; // Flag to track if meeting dialog is open
  private dialogOpenLock: boolean = false; // Lock to prevent simultaneous dialog opens
  private lastMeetingSessionEventKey: string = '';
  private lastMeetingSessionEventAt: number = 0;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private dialog: MatDialog,
    private signalRService: SignalRService,
    private userService: UserService,
    private jwtHelper: JwtHelperService,
    private minimizedMeetingService: MinimizedMeetingService,
    private sanitizer: DomSanitizer,
    cdr: ChangeDetectorRef
  ) {
    this.cdr = cdr;
  }

  ngOnInit(): void {
    this._route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(async params => {
      this.meetingId = params['id'];
      if (this.meetingId) {
        await this.fetchMeetingDetails();
        // Initialize SignalR connection and join meeting automatically
        // This ensures users receive the meeting start event and popup opens automatically for invited users
        await this.initializeSignalR();
        // Check if there's a minimized meeting for this meetingId and restore it
        this.checkAndRestoreMinimizedMeeting();
      }
    });
  }

  getTrustedDecisionHtml(html: string | null | undefined, fallback: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || fallback);
  }

  private async initializeSignalR(): Promise<void> {
    // Ensure SignalR connection is established
    if (!this.signalRService.isConnected()) {
      await this.signalRService.startConnection();
    }

    // Only automatically join the meeting room for hosters (Secretary)
    // AND only if the hoster is actually invited to this meeting.
    // TCM members should join manually by clicking the "Attend" button.
    if (this.canStartMeeting() && this.isUserInvited()) {
      const userId = this.userService.user?.Id || '';
      const userName = this.userService.user?.FullName || 'User';
      await this.signalRService.joinMeeting(this.meetingId, userId, userName);
    }

    // Listen for meeting session start events - AUTO-OPEN POPUP FOR INVITED USERS
    const sessionSub = this.signalRService.meetingSession$.subscribe(
      async (sessionData) => {
        if (sessionData.meetingId === this.meetingId) {
          const statusKey = (sessionData.meetingData?.status || '').toString().toLowerCase();
          const eventKey = `${sessionData.meetingId}:${statusKey}`;
          const now = Date.now();
          if (this.lastMeetingSessionEventKey === eventKey && now - this.lastMeetingSessionEventAt < 5000) {
            console.log('Duplicate meeting session event received recently - skipping');
            return;
          }
          this.lastMeetingSessionEventKey = eventKey;
          this.lastMeetingSessionEventAt = now;

          // Prevent processing multiple rapid events (debounce mechanism)
          if (this.isProcessingMeetingSession) {
            console.log('Already processing a meeting session event - skipping duplicate');
            return;
          }

          // Check if dialog is already open before processing
          // But allow if waiting-attendance-dialog is open (it should close and open join-meeting-dialog)
          let isDialogAlreadyOpen = false;
          let isWaitingDialogOpen = false;
          if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
            try {
              const overlayContainer = document.querySelector('.cdk-overlay-container');
              // Check for admin or join dialogs (these should not be replaced)
              const dialogElement = overlayContainer?.querySelector('.admin-meeting-dialog, .join-meeting-dialog');
              isDialogAlreadyOpen = !!dialogElement;
              // Check if waiting-attendance-dialog is open (this can be replaced)
              const waitingDialog = overlayContainer?.querySelector('.waiting-attendance-dialog');
              isWaitingDialogOpen = !!waitingDialog;
            } catch (e) {
              isDialogAlreadyOpen = true;
            }
          }

          // If admin or join dialog is already open, skip (don't replace)
          // But if only waiting-attendance-dialog is open, allow replacement
          if (isDialogAlreadyOpen && !isWaitingDialogOpen) {
            console.log('Admin or join dialog already open - skipping meeting session event');
            return;
          }
          
          // Also check the flag (but allow if waiting dialog is open)
          if (this.isDialogOpen && isDialogAlreadyOpen && !isWaitingDialogOpen) {
            console.log('Dialog already open (flag check) - skipping meeting session event');
            return;
          }

          this.isProcessingMeetingSession = true;
          console.log('Meeting session event received:', sessionData);
          
          try {
            // Refresh meeting details to get the latest status before opening dialog
            await this.fetchMeetingDetails();
            
            const status = sessionData.meetingData?.status || this.meetingDetails?.status || '';
            const statusLower = status?.toLowerCase() || '';
            const isOnGoing = statusLower === 'ongoing' || statusLower === '3';
            const isWaitingForAttendance = statusLower === 'waitingforattendance' || statusLower === '2';
            
            // Check if user is hoster (will auto-open admin dialog) AND actually invited
            const isHoster = this.canStartMeeting() && this.isUserInvited();
            
            // If status is OnGoing, auto-open dialog for invited members
            if (isOnGoing && this.isUserInvited() && !isHoster) {
              console.log('Meeting status is OnGoing - auto-opening join-meeting-dialog for invited member');
              
              // Close waiting-attendance-dialog if it's open
              if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
                try {
                  const overlayContainer = document.querySelector('.cdk-overlay-container');
                  const waitingDialog = overlayContainer?.querySelector('.waiting-attendance-dialog');
                  if (waitingDialog) {
                    console.log('Closing waiting-attendance-dialog before opening join-meeting-dialog');
                    this.meetingSessionDialogRef.close();
                    this.meetingSessionDialogRef = null;
                    this.isDialogOpen = false;
                    this.cdr.markForCheck();
                    // Wait for dialog to close
                    await new Promise(resolve => setTimeout(resolve, 500));
                  }
                } catch (e) {
                  console.error('Error closing waiting dialog:', e);
                }
              }
              
              // Ensure flags are reset before opening new dialog
              this.isDialogOpen = false;
              this.meetingSessionDialogRef = null;
              
              // Remove from minimized list FIRST (toaster should be hidden when dialog opens)
              this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
              
              // Open the join-meeting-dialog directly for all invited members when status is OnGoing
              console.log('About to call openMeetingSession for join-meeting-dialog');
              setTimeout(() => {
                console.log('Calling openMeetingSession now');
                this.openMeetingSession(sessionData);
                // Reset flag after a delay to allow dialog to open
                setTimeout(() => {
                  this.isProcessingMeetingSession = false;
                }, 1000);
              }, 500);
            } else if (isOnGoing && isHoster) {
              // Hoster always gets admin dialog when status is OnGoing
              console.log('Meeting status is OnGoing - auto-opening admin-meeting-dialog for hoster');
              // Close waiting-attendance-dialog if it's open
              if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
                try {
                  const overlayContainer = document.querySelector('.cdk-overlay-container');
                  const waitingDialog = overlayContainer?.querySelector('.waiting-attendance-dialog');
                  if (waitingDialog) {
                    console.log('Closing waiting-attendance-dialog before opening admin-meeting-dialog');
                    this.meetingSessionDialogRef.close();
                    this.meetingSessionDialogRef = null;
                    this.isDialogOpen = false;
                    // Wait for dialog to close
                    await new Promise(resolve => setTimeout(resolve, 300));
                  }
                } catch (e) {
                  console.error('Error closing waiting dialog:', e);
                }
              }
              // Remove from minimized list FIRST (toaster should be hidden when dialog opens)
              this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
              // Open the admin-meeting-dialog
              setTimeout(() => {
                this.openMeetingSession(sessionData);
                // Reset flag after a delay to allow dialog to open
                setTimeout(() => {
                  this.isProcessingMeetingSession = false;
                }, 1000);
              }, 300);
            } else if (isWaitingForAttendance && this.isUserInvited()) {
              // For WaitingForAttendance status, auto-open the waiting attendance dialog for all invited members
              console.log('Meeting status is WaitingForAttendance - auto-opening waiting attendance dialog for invited user');
              // Remove from minimized list FIRST (toaster should be hidden when dialog opens)
              this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
              
              const meetingTitle = sessionData.meetingTitle || this.meetingDetails?.title || 'Meeting';
              // Open the waiting attendance dialog for all invited members
              setTimeout(() => {
                this.openWaitingAttendanceDialog(meetingTitle);
                // Reset flag after a delay to allow dialog to open
                setTimeout(() => {
                  this.isProcessingMeetingSession = false;
                }, 1000);
              }, 300);
            } else if (!this.isUserInvited()) {
              console.log('User is not invited to this meeting');
              this.isProcessingMeetingSession = false;
            } else {
              this.isProcessingMeetingSession = false;
            }
          } catch (error) {
            console.error('Error processing meeting session event:', error);
            this.isProcessingMeetingSession = false;
          }
        }
        this.cdr.markForCheck();
      }
    );
    this.subscriptions.push(sessionSub);

    // Listen for meeting status changed events (broadcasted to all users)
    const meetingStatusChangedSub = this.signalRService.meetingStatusChanged$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      async (statusData) => {
        // Check if this status change is for the current meeting
        // Compare with both meetingId (string MeetingId) and meetingIdInt/id (integer Id as string)
        const isCurrentMeeting = statusData.meetingId === this.meetingId || 
                                 statusData.meetingIdInt === this.meetingId || 
                                 statusData.id?.toString() === this.meetingId ||
                                 (this.meetingDetails && (
                                   statusData.meetingId === this.meetingDetails.meetingId ||
                                   statusData.meetingIdInt === this.meetingDetails.meetingId ||
                                   statusData.id === this.meetingDetails.id
                                 ));
        
        if (isCurrentMeeting) {
          console.log('Meeting status changed event received:', statusData);
          // Refresh meeting details to get the latest status
          await this.fetchMeetingDetails();
          
          const newStatus = statusData.status?.toLowerCase() || '';
          const isOnGoing = newStatus === 'ongoing' || newStatus === '3';
          const isWaitingForAttendance = newStatus === 'waitingforattendance' || newStatus === '2';
          
          // If status changed to OnGoing, close waiting attendance dialog and open appropriate dialog
          if (isOnGoing) {
            // Close waiting attendance dialog if it's open
            let wasWaitingDialogOpen = false;
            if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
              try {
                const overlayContainer = document.querySelector('.cdk-overlay-container');
                const dialogElement = overlayContainer?.querySelector('.waiting-attendance-dialog');
                if (dialogElement) {
                  console.log('Closing waiting attendance dialog - status changed to OnGoing');
                  wasWaitingDialogOpen = true;
                  this.meetingSessionDialogRef.close({ action: 'statusChanged' });
                  this.meetingSessionDialogRef = null;
                  this.isDialogOpen = false;
                }
              } catch (e) {
                // Ignore errors
              }
            }
            
            // Remove from minimized list
            this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
            
            // If the waiting dialog was open (meaning user was on this page), auto-open the appropriate dialog
            if (wasWaitingDialogOpen) {
              console.log('Status changed to OnGoing - auto-opening meeting dialog for user');
              
              // Wait for the waiting dialog to fully close before opening new dialog
              setTimeout(() => {
                // Reset flags before opening
                this.isDialogOpen = false;
                this.dialogOpenLock = false;
                this.meetingSessionDialogRef = null;
                
                const meetingTitle = this.meetingDetails?.title || statusData.meetingTitle || 'Meeting';
                const agendaItems = this.meetingDetails?.agendaItems || statusData.agendaItems || [];
                const meetingData = {
                  agendaItems: agendaItems,
                  meetingId: this.meetingId,
                  status: 'OnGoing',
                  members: this.meetingDetails?.members || statusData.members || []
                };
                
                // Check if user is hoster AND invited
                const isHoster = this.canStartMeeting();
                
                if (isHoster && this.isUserInvited()) {
                  // Open admin dialog for hoster who is invited
                  console.log('Opening admin dialog for hoster who is invited after status change to OnGoing');
                  this.openAdminMeetingDialog(meetingTitle, meetingData);
                } else if (this.isUserInvited()) {
                  // Open join dialog for invited members
                  console.log('Opening join dialog for invited member after status change to OnGoing via SignalR');
                  this.openMeetingSession({
                    meetingId: this.meetingId,
                    meetingTitle: meetingTitle,
                    meetingData: meetingData
                  });
                }
              }, 300);
            } else {
              console.log('Status changed to OnGoing but waiting dialog was not open - OpenMeetingSession event will handle it if needed');
            }
          } else if (isWaitingForAttendance) {
            // Status is WaitingForAttendance - just update/add to minimized list for toaster
            const minimizedMeetings = this.minimizedMeetingService.getMinimizedMeetings();
            const existingMinimized = minimizedMeetings.find(m => m.meetingId === this.meetingId);
            if (existingMinimized) {
              // Update status
              this.minimizedMeetingService.updateMeetingStatus(this.meetingId, statusData.status);
            } else if (this.isUserInvited()) {
              // If user is invited but meeting not in minimized list, add it for toaster
              this.minimizedMeetingService.addMinimizedMeeting({
                meetingId: this.meetingId,
                meetingTitle: statusData.meetingTitle,
                status: statusData.status,
                dialogType: 'waiting',
                dialogData: {
                  meetingTitle: statusData.meetingTitle,
                  meetingId: this.meetingId,
                  meetingIdInt: this.meetingDetails?.id,
                  id: this.meetingDetails?.id,
                  members: statusData.members || [],
                  agendaItems: statusData.agendaItems || []
                }
              });
            }
          } else {
            // Other status changes - just update toaster
            const minimizedMeetings = this.minimizedMeetingService.getMinimizedMeetings();
            const existingMinimized = minimizedMeetings.find(m => m.meetingId === this.meetingId);
            if (existingMinimized) {
              this.minimizedMeetingService.updateMeetingStatus(this.meetingId, statusData.status);
            }
          }
          
          this.cdr.markForCheck();
        }
      }
    );
    this.subscriptions.push(meetingStatusChangedSub);

    // Listen for meeting ended events
    const meetingEndedSub = this.signalRService.meetingEnded$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (data) => {
        if (data.meetingId === this.meetingId) {
          // First update status to Cancelled so the toaster filters it out immediately
          this.minimizedMeetingService.updateMeetingStatus(this.meetingId, 'Cancelled');
          
          // Remove from minimized list immediately
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
          
          // Also remove any meetings with Completed or Cancelled status from minimized list
          this.minimizedMeetingService.removeMeetingsByStatus(['Completed', '5', 'completed', 'Cancelled', '4', 'cancelled']);
          
          // Refresh meeting details to get updated status
          this.fetchMeetingDetails();
          
          // Close any open dialogs
          if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
            this.meetingSessionDialogRef.close();
            this.meetingSessionDialogRef = null;
          }
        }
        this.cdr.markForCheck();
      }
    );
    this.subscriptions.push(meetingEndedSub);

    // Listen for agenda navigation events to track current agenda index
    // This allows TCM members to join at the same agenda item as the hoster
    const agendaNavSub = this.signalRService.agendaNavigation$.subscribe(
      (navData) => {
        if (navData.meetingId === this.meetingId) {
          console.log('Agenda navigation received, updating current index:', navData.agendaIndex);
          this.currentAgendaIndex = navData.agendaIndex;
          // Note: Backend session already stores this in MeetingCurrentAgendaIndex
        }
      }
    );
    this.subscriptions.push(agendaNavSub);

    // CRITICAL: Listen for meeting attendees updates from SignalR
    // This populates the meetingAttendees array so we can check if user has attended
    const attendeesSub = this.signalRService.meetingAttendees$.subscribe(
      (attendees) => {
        console.log('Meeting attendees updated:', attendees);
        this.meetingAttendees = attendees || [];
        this.cdr.markForCheck();
      }
    );
    this.subscriptions.push(attendeesSub);

    // Listen for restore meeting dialog event (from minimized indicator)
    // Remove any existing listener first to prevent duplicates
    window.removeEventListener('restore-meeting-dialog', this.handleRestoreDialog);
    // Use arrow function to maintain same reference for proper cleanup
    window.addEventListener('restore-meeting-dialog', this.handleRestoreDialog);
  }

  private isRestoringDialog = false; // Flag to prevent duplicate opens

  private hasOpenMeetingDialog(dialogTypes: Array<'admin' | 'join' | 'waiting'> = ['admin', 'join']): boolean {
    const currentMeetingId = this.meetingId?.toString();

    return this.dialog.openDialogs.some((dialogRef) => {
      const instance: any = dialogRef.componentInstance;
      if (!instance) {
        return false;
      }

      const dialogMeetingId = instance.meetingId?.toString();
      if (currentMeetingId && dialogMeetingId && dialogMeetingId !== currentMeetingId) {
        return false;
      }

      return (
        (dialogTypes.includes('admin') && instance instanceof AdminMeetingDialogComponent) ||
        (dialogTypes.includes('join') && instance instanceof JoinMeetingDialogComponent) ||
        (dialogTypes.includes('waiting') && instance instanceof WaitingAttendanceDialogComponent)
      );
    });
  }

  private handleRestoreDialog = (event: any): void => {
    const meeting: any = event.detail;
    if (meeting && meeting.meetingId === this.meetingId) {
      console.log('Restoring meeting dialog:', meeting);
      
      // If meeting details are not loaded yet, wait a bit and retry
      if (!this.meetingDetails) {
        console.log('Meeting details not loaded yet, waiting and retrying...');
        setTimeout(() => {
          this.handleRestoreDialog(event);
        }, 500);
        return;
      }
      
      // Check if dialog is actually open by checking if overlay exists
      let isDialogActuallyOpen = false;
      if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
        try {
          // Check if the dialog overlay container exists in DOM
          const overlayContainer = document.querySelector('.cdk-overlay-container');
          const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
          isDialogActuallyOpen = !!dialogElement;
        } catch (e) {
          // If check fails, assume dialog might be open
          isDialogActuallyOpen = true;
        }
      }
      
      if (isDialogActuallyOpen) {
        console.log('Dialog already open, bringing to front');
        return;
      }
      
      // Prevent duplicate opens (only if currently restoring)
      if (this.isRestoringDialog) {
        console.log('Already restoring dialog, ignoring duplicate request');
        return;
      }
      
      // Clear any stale reference
      if (this.meetingSessionDialogRef && !isDialogActuallyOpen) {
        console.log('Clearing stale dialog reference');
        this.meetingSessionDialogRef = null;
      }
      
      // Set flag to prevent duplicate opens
      this.isRestoringDialog = true;
      
      // Remove from minimized list since we're restoring it
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
      
      // Refresh meeting details to ensure we have the latest status
      this.fetchMeetingDetails().then(() => {
        // Small delay to ensure meeting details are fully processed
        setTimeout(() => {
          // Check again before opening (in case dialog was opened during fetch)
          let isStillOpen = false;
          if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
            try {
              const overlayContainer = document.querySelector('.cdk-overlay-container');
              const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
              isStillOpen = !!dialogElement;
            } catch (e) {
              isStillOpen = true;
            }
          }
          
          if (isStillOpen) {
            console.log('Dialog opened during fetch, skipping restore');
            this.isRestoringDialog = false;
            return;
          }
          
          // Check if user is the hoster (same logic as joinMeeting)
          if (!this.meetingDetails) {
            console.error('Cannot restore dialog: Meeting details not available');
            this.isRestoringDialog = false;
            return;
          }

          // Get user role and name
          let userRole = this.userService.user?.Role;
          const userName = this.userService.user?.FullName || '';
          
          // Fallback: Try to read role directly from token if not in user object
          if (!userRole && this.userService.token) {
            try {
              const decodedToken = this.jwtHelper.decodeToken(this.userService.token);
              userRole = decodedToken?.['Role'] || decodedToken?.['role'] || decodedToken?.[AppConstants.CLAIMS.Role];
            } catch (e) {
              console.warn('Could not decode token for role:', e);
            }
          }

          // Check if user is the hoster (Secretary who created the meeting AND is invited)
          const isHoster = this.canStartMeeting();

          // Check status (can be string or number)
          const status = this.meetingDetails.status;
          const isWaitingForAttendance = status === 'WaitingForAttendance' || status === 2 || status === '2';
          const isOnGoing = status === 'OnGoing' || status === 3 || status === '3';
          const isCancelled = status === 'Cancelled' || status === 4 || status === '4';
          const isCompleted = status === 'Completed' || status === 5 || status === '5';

          console.log('handleRestoreDialog - User check:', {
            userRole,
            userName,
            isHoster,
            status,
            isWaitingForAttendance,
            isOnGoing,
            isCancelled,
            isCompleted
          });

          // Don't restore if meeting is cancelled or completed
          if (isCancelled || isCompleted) {
            console.log('Cannot restore dialog: Meeting is cancelled or completed');
            this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
            this.isRestoringDialog = false;
            return;
          }

          // Open the appropriate dialog based on user role and status (NOT based on stored dialogType)
          // Rule: If WaitingForAttendance meeting and user is invited -> show waiting-attendance-dialog
          if (isWaitingForAttendance && this.isUserInvited()) {
            const meetingTitle = this.meetingDetails?.title || meeting.meetingTitle || 'Meeting';
            console.log('Restoring waiting-attendance-dialog for invited member');
            this.openWaitingAttendanceDialog(meetingTitle);
            this.isRestoringDialog = false;
            return;
          }

          // Rule: If OnGoing meeting and user is hoster AND invited -> show admin-meeting-dialog
          if (isHoster && isOnGoing && this.isUserInvited()) {
            const agendaItems = this.meetingDetails?.agendaItems || meeting.dialogData?.agendaItems || [];
            const meetingTitle = this.meetingDetails?.title || meeting.meetingTitle || 'Meeting';
            const meetingData = {
              ...(meeting.dialogData || {}),
              agendaItems: agendaItems,
              meetingId: this.meetingId,
              meetingIdInt: this.meetingDetails?.id,
              id: this.meetingDetails?.id,
              currentAgendaIndex: meeting.dialogData?.currentAgendaIndex ?? this.currentAgendaIndex ?? 0,
              meetingStatus: status,
              meetingMembers: this.meetingDetails?.members || meeting.dialogData?.meetingMembers || []
            };
            console.log('Restoring admin-meeting-dialog for hoster who is invited');
            this.openAdminMeetingDialog(meetingTitle, meetingData);
            this.isRestoringDialog = false;
            return;
          }

          // Rule: If OnGoing meeting and user is a member (not hoster) -> show join-meeting-dialog
          if (isOnGoing && !isHoster) {
            const agendaItems = this.meetingDetails?.agendaItems || meeting.dialogData?.agendaItems || [];
            const meetingTitle = this.meetingDetails?.title || meeting.meetingTitle || 'Meeting';
            console.log('Restoring join-meeting-dialog for member', { isOnGoing, isHoster, isInvited: this.isUserInvited() });
            const dialogRef = this.dialog.open(JoinMeetingDialogComponent, {
              width: '1100px',
              maxWidth: '95vw',
              maxHeight: '95vh',
              disableClose: false,
              panelClass: 'join-meeting-dialog',
              data: {
                ...(meeting.dialogData || {}),
                meetingTitle: meetingTitle,
                agendaItems: agendaItems,
                currentAgendaIndex: meeting.dialogData?.currentAgendaIndex ?? this.currentAgendaIndex ?? 0,
                meetingId: this.meetingId
              }
            });
            this.meetingSessionDialogRef = dialogRef;
            this.isRestoringDialog = false; // Reset flag after opening
            this.isDialogOpen = true; // Set flag when dialog opens
            
            dialogRef.afterClosed().subscribe(result => {
              // If minimized, enable button
              if (result?.action === 'minimized') {
                this.isDialogOpen = false;
                this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
                return;
              }
              // If actually closed (not minimized), check if meeting is still active
              if (result !== 'minimized' && result?.action !== 'minimized') {
                // Check if meeting is still active (OnGoing or WaitingForAttendance)
                const isActive = this.meetingDetails?.status === 'OnGoing' || 
                               this.meetingDetails?.status === 'WaitingForAttendance' ||
                               this.meetingDetails?.status === 3 || 
                               this.meetingDetails?.status === 2;
                
                if (isActive) {
                  // Meeting is still active, add back to minimized list (user clicked outside)
                  const meetingTitle = this.meetingDetails?.title || 'Meeting';
                  const agendaItems = this.meetingDetails?.agendaItems || [];
                  this.minimizedMeetingService.addMinimizedMeeting({
                    meetingId: this.meetingId,
                    meetingTitle: meetingTitle,
                    status: this.meetingDetails?.status?.toString() || 'OnGoing',
                    dialogType: 'join',
                    dialogData: {
                      meetingTitle: meetingTitle,
                      agendaItems: agendaItems,
                      currentAgendaIndex: this.currentAgendaIndex || 0, // Use current agenda index where hoster is
                      meetingId: this.meetingId,
                      meetingStatus: this.meetingDetails?.status
                    }
                  });
                } else {
                  // Meeting is not active, remove from minimized list
                  this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
                }
              }
              this.meetingSessionDialogRef = null;
              this.isRestoringDialog = false; // Ensure flag is reset
              this.isDialogOpen = false; // Enable button when dialog closes
            });
          } else {
            // Meeting is not in an active state, or user role/status doesn't match any dialog type
            console.warn('Cannot restore dialog: Meeting is not in an active state or user role mismatch', {
              isWaitingForAttendance,
              isOnGoing,
              isHoster,
              isInvited: this.isUserInvited(),
              status,
              userRole,
              userName
            });
            this.isRestoringDialog = false;
          }
        }, 100);
      }).catch((error) => {
        console.error('Error restoring dialog:', error);
        this.isRestoringDialog = false;
      });
    }
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    // Remove event listener
    window.removeEventListener('restore-meeting-dialog', this.handleRestoreDialog);
  }

  // Check if there's a minimized meeting for this meetingId and restore it
  private async checkAndRestoreMinimizedMeeting(): Promise<void> {
    if (!this.meetingId || !this.meetingDetails) {
      console.log('Cannot check minimized meeting: missing meetingId or meetingDetails');
      return;
    }

    // Check if dialog is already open - if so, don't open it again
    if (this.isDialogOpen) {
      console.log('Dialog is already open, skipping restore');
      return;
    }

    // Also check if dialog is actually open in the DOM
    let isDialogActuallyOpen = false;
    try {
      const overlayContainer = document.querySelector('.cdk-overlay-container');
      const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
      isDialogActuallyOpen = !!dialogElement;
    } catch (e) {
      isDialogActuallyOpen = false;
    }
    
    if (isDialogActuallyOpen) {
      console.log('Dialog is already open in DOM, skipping restore');
      this.isDialogOpen = true; // Sync the flag
      return;
    }

    // Check if meeting is cancelled or completed - don't restore if so
    const status = this.meetingDetails?.status;
    const isCancelled = status === 'Cancelled' || status === 4 || status === '4';
    const isCompleted = status === 'Completed' || status === 5 || status === '5';
    
    if (isCancelled || isCompleted) {
      console.log('Meeting is cancelled or completed, not restoring dialog');
      // Remove from minimized list if it exists
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
      return;
    }

    // Check if there's a minimized meeting for this meetingId
    const minimizedMeetings = this.minimizedMeetingService.getMinimizedMeetings();
    const minimizedMeeting = minimizedMeetings.find(m => m.meetingId === this.meetingId);
    
    // Check if meeting is in active status (WaitingForAttendance or OnGoing)
    const isWaitingForAttendance = status === 'WaitingForAttendance' || status === 2 || status === '2';
    const isOnGoing = status === 'OnGoing' || status === 3 || status === '3';
    const isActiveStatus = isWaitingForAttendance || isOnGoing;
    
    // If no minimized meeting AND status is not active, don't restore
    if (!minimizedMeeting && !isActiveStatus) {
      console.log('No minimized meeting and meeting is not active, not restoring dialog');
      return;
    }

    // CRITICAL: Fetch current attendees list from SignalR to check if user has attended
    // This is needed because meetingAttendees array is empty on page refresh
    try {
      console.log('Fetching meeting attendees from SignalR...');
      await this.signalRService.getMeetingAttendees(this.meetingId);
      // Wait a bit for the attendees list to be received
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('Current meeting attendees:', this.meetingAttendees);
    } catch (error) {
      console.error('Error fetching meeting attendees:', error);
    }

    // Remove from minimized list FIRST to prevent toaster from showing (if exists)
    if (minimizedMeeting) {
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
    }
    
    console.log('Page refreshed - auto-restoring dialog for active meeting without showing toaster');
    
    // Use requestAnimationFrame to open dialog on next frame, ensuring removal is processed
    // This prevents the toaster from briefly appearing
    requestAnimationFrame(() => {
      // Check if dialog is already open
      let isDialogActuallyOpen = false;
      if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
        try {
          const overlayContainer = document.querySelector('.cdk-overlay-container');
          const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
          isDialogActuallyOpen = !!dialogElement;
        } catch (e) {
          isDialogActuallyOpen = true;
        }
      }
      
      if (isDialogActuallyOpen) {
        console.log('Dialog already open, skipping restore');
        return;
      }

      // Prevent duplicate opens
      if (this.isRestoringDialog) {
        console.log('Already restoring dialog, ignoring duplicate request');
        return;
      }

      this.isRestoringDialog = true;

      // Get user role and name
      let userRole = this.userService.user?.Role;
      const userName = this.userService.user?.FullName || '';
      
      // Fallback: Try to read role directly from token if not in user object
      if (!userRole && this.userService.token) {
        try {
          const decodedToken = this.jwtHelper.decodeToken(this.userService.token);
          userRole = decodedToken?.['Role'] || decodedToken?.['role'] || decodedToken?.[AppConstants.CLAIMS.Role];
        } catch (e) {
          console.warn('Could not decode token for role:', e);
        }
      }

      // Check if user is the hoster (Secretary who created the meeting AND is invited)
      const isHoster = this.canStartMeeting();

      // Check status (can be string or number)
      const isWaitingForAttendance = status === 'WaitingForAttendance' || status === 2 || status === '2';
      const isOnGoing = status === 'OnGoing' || status === 3 || status === '3';

      // Check if user has attended the meeting (clicked Attend button)
      const userId = this.userService.user?.Id?.toString() || '';
      const hasUserAttended = this.meetingAttendees.some(attendee => 
        attendee.userId?.toString() === userId || 
        (attendee.userName && userName && attendee.userName.toLowerCase() === userName.toLowerCase())
      );

      // Open the appropriate dialog based on user role and status
      // Rule: If WaitingForAttendance meeting and user is invited -> show waiting-attendance-dialog
      if (isWaitingForAttendance && this.isUserInvited()) {
        const meetingTitle = this.meetingDetails?.title || (minimizedMeeting ? minimizedMeeting.meetingTitle : 'Meeting');
        console.log('Auto-restoring waiting-attendance-dialog for invited member');
        this.openWaitingAttendanceDialog(meetingTitle);
        this.isRestoringDialog = false;
        return;
      }

      // Rule: If OnGoing meeting and user is hoster AND invited -> show admin-meeting-dialog
      if (isHoster && isOnGoing && this.isUserInvited()) {
        const agendaItems = this.meetingDetails?.agendaItems || (minimizedMeeting ? minimizedMeeting.dialogData?.agendaItems : []) || [];
        const meetingTitle = this.meetingDetails?.title || (minimizedMeeting ? minimizedMeeting.meetingTitle : 'Meeting');
        const meetingData = {
          ...(minimizedMeeting?.dialogData || {}),
          agendaItems: agendaItems,
          meetingId: this.meetingId,
          meetingIdInt: this.meetingDetails?.id,
          id: this.meetingDetails?.id,
          currentAgendaIndex: minimizedMeeting?.dialogData?.currentAgendaIndex ?? this.currentAgendaIndex ?? 0,
          meetingStatus: status,
          meetingMembers: this.meetingDetails?.members || minimizedMeeting?.dialogData?.meetingMembers || []
        };
        console.log('Auto-restoring admin-meeting-dialog for hoster who is invited');
        this.openAdminMeetingDialog(meetingTitle, meetingData);
        this.isRestoringDialog = false;
        return;
      }

      // Rule: If OnGoing meeting and user is a member (not hoster) -> show join-meeting-dialog
      // BUT ONLY if they have attended (clicked Attend button)
      if (isOnGoing && !isHoster && this.isUserInvited()) {
        if (!hasUserAttended) {
          console.log('User has not attended meeting yet - not auto-opening dialog');
          this.isRestoringDialog = false;
          return;
        }
        
        const agendaItems = this.meetingDetails?.agendaItems || (minimizedMeeting ? minimizedMeeting.dialogData?.agendaItems : []) || [];
        const meetingTitle = this.meetingDetails?.title || (minimizedMeeting ? minimizedMeeting.meetingTitle : 'Meeting');
        console.log('Auto-restoring join-meeting-dialog for member');
        const dialogRef = this.dialog.open(JoinMeetingDialogComponent, {
          width: '1100px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          disableClose: false,
          panelClass: 'join-meeting-dialog',
          data: {
            ...(minimizedMeeting?.dialogData || {}),
            meetingTitle: meetingTitle,
            agendaItems: agendaItems,
            currentAgendaIndex: minimizedMeeting?.dialogData?.currentAgendaIndex ?? this.currentAgendaIndex ?? 0,
            meetingId: this.meetingId
          }
        });
        this.meetingSessionDialogRef = dialogRef;
        this.isRestoringDialog = false;
        this.isDialogOpen = true; // Set flag when dialog opens
        
        dialogRef.afterClosed().subscribe(result => {
          // Always enable button when dialog closes (handles all close scenarios)
          this.isDialogOpen = false;
          this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          
          // If minimized, return early (button already enabled above)
          if (result?.action === 'minimized') {
            return;
          }
          // If actually closed (not minimized), check if meeting is still active
          if (result !== 'minimized' && result?.action !== 'minimized') {
            // Check if meeting is still active (OnGoing or WaitingForAttendance)
            const isActive = this.meetingDetails?.status === 'OnGoing' || 
                           this.meetingDetails?.status === 'WaitingForAttendance' ||
                           this.meetingDetails?.status === 3 || 
                           this.meetingDetails?.status === 2;
            
            if (isActive) {
              // Add back to minimized list so toaster can show it
              this.minimizedMeetingService.addMinimizedMeeting({
                meetingId: this.meetingId,
                meetingTitle: meetingTitle,
                status: this.meetingDetails?.status || 'OnGoing',
                dialogType: 'join',
                dialogData: {
                  meetingTitle: meetingTitle,
                  meetingId: this.meetingId,
                  meetingIdInt: this.meetingDetails?.id,
                  id: this.meetingDetails?.id,
                  agendaItems: agendaItems
                }
              });
            }
            this.meetingSessionDialogRef = null;
            this.isDialogOpen = false; // Enable button when dialog closes
            this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          }
        });
        return;
      }

      // If we get here, conditions weren't met to restore dialog
      console.log('Conditions not met to auto-restore dialog');
      this.isRestoringDialog = false;
    }); // Use requestAnimationFrame instead of setTimeout
  }

  private getCurrentMeetingMember(): any | null {
    const currentUserId = this.userService.user?.Id?.toString() || '';
    const userEmail = (this.userService.user?.EmailAddress || '').toString().trim().toLowerCase();
    const members = this.meetingDetails?.members || [];

    return members.find((member: any) => {
      const memberUserId = (member.userId ?? member.UserId ?? member.id ?? member.Id ?? '').toString();
      const memberEmail = (member.email ?? member.userEmail ?? member.EmailAddress ?? member.UserEmail ?? '').toString().trim().toLowerCase();

      return (currentUserId && memberUserId === currentUserId) || (userEmail && memberEmail === userEmail);
    }) || null;
  }

  private isUserInvited(): boolean {
    const member = this.getCurrentMeetingMember();
    const currentUserId = this.userService.user?.Id?.toString() || '';
    const userEmail = (this.userService.user?.EmailAddress || '').toString().trim().toLowerCase();
    const invitedEmails = this.meetingDetails?.invitedMembersEmails || [];
    const invitedEmailsLower = invitedEmails.map((email: any) => (email || '').toString().trim().toLowerCase());
    const isInvited = !!member || invitedEmailsLower.includes(userEmail);

    console.log('Checking if user is invited:', {
      currentUserId,
      userEmail,
      matchedMember: member,
      invitedEmails: invitedEmailsLower,
      isInvited
    });

    return isInvited;
  }

  private isUserAcceptedInvite(): boolean {
    const member = this.getCurrentMeetingMember();

    if (!member) {
      return false;
    }

    const status = (member.status ?? member.statusName ?? member.StatusName ?? member.Status ?? '').toString().trim().toLowerCase();
    return status === 'accepted' || status === '1';
  }

  canJoinMeeting(): boolean {
    if (!this.meetingDetails) {
      return false;
    }

    const status = this.meetingDetails.status;
    const isWaitingForAttendance = status === 'WaitingForAttendance' || status === 2 || status === '2';
    const isOnGoing = status === 'OnGoing' || status === 3 || status === '3';

    return (isWaitingForAttendance || isOnGoing)
      && this.isUserInvited()
      && (this.isUserAcceptedInvite() || this.canStartMeeting());
  }

  private openMeetingSession(sessionData: any): void {
    if (this.hasOpenMeetingDialog(['admin', 'join'])) {
      console.log('Meeting dialog is already open for this meeting - skipping duplicate open');
      this.isDialogOpen = true;
      this.dialogOpenLock = false;
      return;
    }

    // Check lock first - if locked, skip
    if (this.dialogOpenLock) {
      console.log('Dialog opening already in progress (lock active) - skipping');
      return;
    }
    
    // Check if dialog is already open using flag
    if (this.isDialogOpen) {
      console.log('Dialog already open (flag check) - skipping');
      return;
    }
    
    // Prevent opening multiple dialogs for the same meeting
    if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
      try {
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        const dialogElement = overlayContainer?.querySelector('.admin-meeting-dialog, .join-meeting-dialog');
        if (dialogElement) {
          console.log('Meeting session dialog already open (DOM check) - skipping');
          return;
        }
      } catch (e) {
        // If check fails, continue
      }
    }

    // Check if meeting is cancelled or completed - don't open dialog if so
    const status = this.meetingDetails?.status;
    const isCancelled = status === 'Cancelled' || status === 4 || status === '4';
    const isCompleted = status === 'Completed' || status === 5 || status === '5';
    
    if (isCancelled || isCompleted) {
      console.log('Meeting is cancelled or completed, not opening dialog');
      // Remove from minimized list if it exists
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
      return;
    }

    console.log('Auto-opening meeting dialog for invited user');
    
    // Remove from minimized list when opening dialog (toaster should be hidden when dialog is open)
    this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);

    // Check if user is the hoster (Secretary who created the meeting AND is invited)
    const isHoster = this.canStartMeeting();
    
    console.log('Is Hoster (canStartMeeting):', isHoster, 'Status:', this.meetingDetails?.status);
    
    // If WaitingForAttendance status: Don't open dialog - toaster will handle it
    // UNLESS we're being called with explicit status in sessionData
    // Check for both string and numeric status values
    const meetingStatus = sessionData?.meetingData?.status || this.meetingDetails?.status;
    const isWaitingForAttendance = meetingStatus === 'WaitingForAttendance' || 
                                   meetingStatus === '2' || 
                                   meetingStatus === 2 ||
                                   meetingStatus?.toString() === '2';
    
    console.log('openMeetingSession - meetingStatus:', meetingStatus, 'isWaitingForAttendance:', isWaitingForAttendance, 'sessionData.meetingData.status:', sessionData?.meetingData?.status);
    
    if (isWaitingForAttendance) {
      // Don't open dialog - toaster will show the notification
      console.log('Meeting is WaitingForAttendance - toaster will handle notification');
      return;
    }

    // For OnGoing status: Hoster sees admin-dialog, others see join-meeting-dialog
    // Explicitly check for OnGoing status (could be 'OnGoing' or '3')
    const isOnGoing = meetingStatus === 'OnGoing' || meetingStatus === '3' || meetingStatus === 3;
    
    console.log('Current meeting status:', meetingStatus, 'Is OnGoing:', isOnGoing);
    
    if (isOnGoing) {
      if (isHoster && this.isUserInvited()) {
        console.log('Hoster who is invited - opening admin-meeting-dialog for OnGoing status');
        this.openAdminMeetingDialog(sessionData.meetingTitle, sessionData.meetingData);
      } else {
        console.log('Non-hoster user or not invited - opening join-meeting-dialog for OnGoing status');
        this.dialogOpenLock = true;
        const dialogRef = this.dialog.open(JoinMeetingDialogComponent, {
          width: '1100px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          disableClose: false,
          panelClass: 'join-meeting-dialog',
            data: {
              meetingTitle: sessionData.meetingTitle,
              agendaItems: sessionData.meetingData?.agendaItems || [],
              currentAgendaIndex: this.currentAgendaIndex || 0, // Use current agenda index where hoster is
              meetingId: sessionData.meetingId,
              meetingStatus: this.meetingDetails?.status // Pass meeting status
            }
        });

        this.meetingSessionDialogRef = dialogRef;
        this.isDialogOpen = true; // Set flag when dialog opens
        this.dialogOpenLock = false; // Release lock after setting flags

        dialogRef.afterClosed().subscribe(result => {
          console.log('Join meeting dialog closed', result);
          // Always enable button when dialog closes (handles all close scenarios)
          this.isDialogOpen = false;
          this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
          
          // If minimized, return early (button already enabled above)
          if (result?.action === 'minimized') {
            return;
          }
          // If actually closed (not minimized), check if meeting is still active
          if (result !== 'minimized' && result?.action !== 'minimized') {
            // Check if meeting is still active (OnGoing or WaitingForAttendance)
            const isActive = this.meetingDetails?.status === 'OnGoing' || 
                           this.meetingDetails?.status === 'WaitingForAttendance' ||
                           this.meetingDetails?.status === 3 || 
                           this.meetingDetails?.status === 2;
            
            if (isActive) {
              // Meeting is still active, add back to minimized list (user clicked outside)
              const meetingTitle = this.meetingDetails?.title || 'Meeting';
              const agendaItems = this.meetingDetails?.agendaItems || [];
              this.minimizedMeetingService.addMinimizedMeeting({
                meetingId: this.meetingId,
                meetingTitle: meetingTitle,
                status: this.meetingDetails?.status?.toString() || 'OnGoing',
                dialogType: 'join',
                dialogData: {
                  meetingTitle: meetingTitle,
                  agendaItems: agendaItems,
                  currentAgendaIndex: this.currentAgendaIndex || 0, // Use current agenda index where hoster is
                  meetingId: this.meetingId,
                  meetingStatus: this.meetingDetails?.status
                }
              });
            } else {
              // Meeting is not active, remove from minimized list
              this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
            }
          }
          this.meetingSessionDialogRef = null; // Reset when dialog is closed
          this.isDialogOpen = false; // Enable button when dialog closes
          this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
        });
      }
    } else {
      console.warn('Meeting status is not OnGoing, current status:', meetingStatus);
      // Fallback: If status is not OnGoing but user is hoster AND invited, still open admin dialog
      if (isHoster && this.isUserInvited()) {
        console.log('Hoster who is invited - opening admin-meeting-dialog (fallback - status may not be updated yet)');
        this.openAdminMeetingDialog(sessionData.meetingTitle, sessionData.meetingData);
      } else {
        this.dialogOpenLock = false;
      }
    }
  }

  // ==================== Data Fetching ====================
  private resolveAgendaDescription(agendaItem: any, language: 'en' | 'ar'): string {
    const keys = language === 'en'
      ? ['descriptionEnglish', 'DescriptionEnglish', 'agendaDescriptionEN', 'AgendaDescriptionEN', 'agendaDescriptionEn', 'descriptionEN', 'DescriptionEN']
      : ['descriptionArabic', 'DescriptionArabic', 'agendaDescriptionAR', 'AgendaDescriptionAR', 'agendaDescriptionAr', 'descriptionAR', 'DescriptionAR'];

    for (const key of keys) {
      const value = agendaItem?.[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    return '';
  }

  async fetchMeetingDetails(): Promise<void> {
    this._loaderService.showLoader();
    
    try {
      const url = `${environment.API_URL}/api/Meetings/${this.meetingId}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        const meeting = response.data;
        
        // Map backend response to component format
        const meetingDate = new Date(meeting.dateTime);
        const formattedDateTime = meetingDate.toISOString();
        
        // Map members (including observers)
        const members = meeting.members?.map((m: any) => ({
          name: m.userName,
          role: m.role, // This should include "Observer" if the user is an observer
          initials: this.getInitials(m.userName),
          status: m.statusName || m.status || 'Pending', // Use status from API (Pending, Accepted, or Rejected)
          userId: m.userId,
          email: m.userEmail
        })) || [];
        
        // Debug: Log all members including observers
        console.log('All members from API (including observers):', members);
        
        // Map agenda items
        const agendaItems = meeting.agendaItems?.map((ai: any, index: number) => ({
          id: ai.id || ai.agendaItemId || 0, // Include ID for matching with decisions
          agendaItemId: ai.id || ai.agendaItemId || 0, // Also include as agendaItemId
          order: ai.order || (index + 1), // Include Order property (default to index + 1 if not provided)
          subject: ai.subject,
          agendaTitleAR: ai.agendaTitleAR,
          agendaTitleEN: ai.agendaTitleEN,
          participants: members.map((m: any) => m.name).join(', '),
          dateTime: this.calculateAgendaItemTime(meetingDate, index),
          duration: '30 mins', // Default duration
          presenter: members[0]?.name || '',
          status: 'Pending',
          descriptionEnglish: this.resolveAgendaDescription(ai, 'en'),
          descriptionArabic: this.resolveAgendaDescription(ai, 'ar'),
          prIdentifiers: ai.prIdentifiers || [],
          currentStage: ai.currentStage,
          requiredFromTC: ai.requiredFromTC,
          department: ai.department,
          agendaDescriptionEN: this.resolveAgendaDescription(ai, 'en'),
          agendaDescriptionAR: this.resolveAgendaDescription(ai, 'ar'),
          tcNotes: ai.tcNotes || '',
          tcDecisionEN: ai.tcDecisionEN || ai.tcDecisionEn || '', // Include TC Decision EN
          tcDecisionAR: ai.tcDecisionAR || ai.tcDecisionAr || ''  // Include TC Decision AR
        })) || [];
        
        // Sort agenda items by Order (ascending - Item #1 first, Item #2 second, etc.)
        agendaItems.sort((a: any, b: any) => (a.order || 999) - (b.order || 999));
        
        // Use status from backend - don't auto-change status based on date
        // Status should only change when explicitly set (Started -> OnGoing, or explicitly Completed)
        const status = meeting.statusName || 'Draft';
        
        // Extract invited members emails from members list
        const invitedMembersEmails = members.map((m: any) => m.email).filter((email: string) => email);
        
        this.meetingDetails = {
          meetingId: meeting.meetingId,
          id: meeting.id,
          title: meeting.meetingTitle,
          dateTime: formattedDateTime,
          duration: '2 hours', // Default duration
          location: meeting.location,
          status: status,
          createdBy: meeting.createdBy || 'System',
          createdDate: meeting.createdAt ? new Date(meeting.createdAt).toISOString().split('T')[0] : '',
          chairmanName: meeting.chairmanName,
          agendaNotes: meeting.agendaNotes,
          isEmergencyMeeting: meeting.isEmergencyMeeting || false,
          members: members,
          agendaItems: agendaItems,
          invitedMembersEmails: invitedMembersEmails,
          memberAttendances: meeting.memberAttendances || [] // Store attendance data from API
        };
        
        // Build attendance table from members and attendance data
        await this.buildAttendanceTable();
        
        // Build agenda items with decisions
        await this.buildAgendaItemsWithDecisions();
        
        // Load meeting attachments
        await this.loadMeetingAttachments();
        
        // Don't open dialog automatically - toaster will handle it
        
        // Trigger change detection for OnPush strategy
        this.cdr.markForCheck();
      } else {
        console.error('Failed to fetch meeting details:', response?.errorMessageEn);
        this.meetingDetails = null;
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error fetching meeting details:', error);
      this.meetingDetails = null;
      this.cdr.markForCheck();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  // Build attendance table from meeting members and saved attendance data
  private async buildAttendanceTable(): Promise<void> {
    // Initialize as empty - only populate if attendance data exists
    this.attendanceTableData = [];

    if (!this.meetingDetails?.members || !this.meetingDetails?.id) {
      return;
    }

    try {
      // Fetch attendance data from API if meeting is completed
      let attendanceData: any[] = [];
      if (this.meetingDetails.id && (this.meetingDetails.status === 'Completed' || this.meetingDetails.status === 5 || this.meetingDetails.status === '5')) {
        try {
          const url = `${environment.API_URL}/api/Meetings/${this.meetingDetails.id}/attendance`;
          const response: any = await lastValueFrom(this.apiService.getByUrl(url));
          if (response?.isSuccess && response?.data && Array.isArray(response.data) && response.data.length > 0) {
            attendanceData = response.data;
          } else {
            // No attendance data stored - return early
            this.attendanceTableData = [];
            return;
          }
        } catch (error) {
          console.warn('Could not fetch attendance data:', error);
          // No attendance data available - return early
          this.attendanceTableData = [];
          return;
        }
      } else {
        // For non-completed meetings, check if memberAttendances exist
        if (this.meetingDetails.memberAttendances && Array.isArray(this.meetingDetails.memberAttendances) && this.meetingDetails.memberAttendances.length > 0) {
          attendanceData = this.meetingDetails.memberAttendances;
        } else {
          // No attendance data - return early
          this.attendanceTableData = [];
          return;
        }
      }

      // Only build table if we have attendance data
      if (!attendanceData || attendanceData.length === 0) {
        this.attendanceTableData = [];
        return;
      }

      // Map role names to Arabic
      const roleMap: { [key: string]: { ar: string; en: string } } = {
        'Chairman': { ar: 'رئيس اللجنة', en: 'Chairman' },
        'Deputy Chairman': { ar: 'نائب رئيس اللجنة', en: 'Deputy Chairman' },
        'ViceChairman': { ar: 'نائب رئيس اللجنة', en: 'Deputy Chairman' },
        'Member': { ar: 'عضو اللجنة', en: 'Member' },
        'TCM': { ar: 'عضو اللجنة', en: 'Member' },
        'SAB Representative': { ar: 'ممثل ديوان المحاسبة', en: 'SAB Representative' },
        'Tender Committee Secretary': { ar: 'أمين سر اللجنة', en: 'Tender Committee Secretary' },
        'Secretary': { ar: 'أمين سر اللجنة', en: 'Tender Committee Secretary' },
        'Observer': { ar: 'مراقب', en: 'Observer' }
      };

      // Build attendance table only from attendance data records
      this.attendanceTableData = attendanceData.map((attendanceRecord: any, index: number) => {
        // Find corresponding member to get additional info
        const member = this.meetingDetails.members.find((m: any) => 
          m.userId === attendanceRecord.userId || 
          m.id === attendanceRecord.userId ||
          (attendanceRecord.userName && m.name && attendanceRecord.userName.toLowerCase() === m.name.toLowerCase())
        );

        const role = attendanceRecord.role || member?.role || 'Member';
        const roleInfo = roleMap[role] || { ar: 'عضو اللجنة', en: role };

        // Get Arabic name from attendance record (from User table) or member data
        const memberNameAr = attendanceRecord.userNameAr || 
                            member?.nameAr || 
                            member?.nameArabic || 
                            attendanceRecord.userName || '';
        const memberNameEn = attendanceRecord.userName || 
                            member?.name || 
                            member?.nameEnglish || 
                            '';

        return {
          number: index + 1,
          memberNameArabic: memberNameAr || memberNameEn, // Fallback to English if Arabic not available
          memberNameEnglish: memberNameEn,
          roleArabic: roleInfo.ar,
          roleEnglish: roleInfo.en,
          attendance: attendanceRecord.attended ?? false
        };
      });

      // Sort attendance table by role: Chairman -> Vice Chairman -> Secretary -> TCM -> Observer
      this.attendanceTableData.sort((a, b) => {
        const roleOrder: { [key: string]: number } = {
          'chairman': 1,
          'vicechairman': 2,
          'vice chairman': 2,
          'deputy chairman': 2,
          'secretary': 3,
          'sec': 3,
          'tender committee secretary': 3,
          'tcm': 4,
          'member': 4,
          'observer': 5
        };
        
        const roleA = (a.roleEnglish || '').toLowerCase().replace(/\s+/g, '');
        const roleB = (b.roleEnglish || '').toLowerCase().replace(/\s+/g, '');
        
        const orderA = roleOrder[roleA] || 4; // Default to TCM if role not found
        const orderB = roleOrder[roleB] || 4;
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // If same role, sort alphabetically by name
        return (a.memberNameEnglish || '').localeCompare(b.memberNameEnglish || '');
      });
      
      // Update numbers after sorting
      this.attendanceTableData.forEach((member, index) => {
        member.number = index + 1;
      });

      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error building attendance table:', error);
      this.attendanceTableData = [];
    }
  }

  // Build agenda items with decisions and voting status
  private async buildAgendaItemsWithDecisions(): Promise<void> {
    if (!this.meetingDetails?.id || !this.meetingDetails?.agendaItems) {
      this.agendaItemsWithDecisions = [];
      return;
    }

    try {
      // Fetch decisions and responses from API (for any meeting that has data)
      let decisionsData: any = null;
      try {
        const url = `${environment.API_URL}/api/Meetings/${this.meetingDetails.id}/decisions`;
        const response: any = await lastValueFrom(this.apiService.getByUrl(url));
        if (response?.isSuccess && response?.data) {
          decisionsData = response.data;
          console.log('Decisions data fetched:', decisionsData);
        }
      } catch (error) {
        console.warn('Could not fetch decisions data:', error);
      }

      // Use agendaItems from API response if available, otherwise use meetingDetails.agendaItems
      const apiAgendaItems = decisionsData?.agendaItems || [];
      const localAgendaItems = this.meetingDetails.agendaItems || [];
      
      // Merge API agenda items with local ones, prioritizing API data
      const allAgendaItems = apiAgendaItems.length > 0 
        ? apiAgendaItems.map((apiItem: any) => {
            // Find matching local item to get additional properties
            const localItem = localAgendaItems.find((li: any) => 
              (li.id && apiItem.agendaId && li.id === apiItem.agendaId) ||
              (li.agendaItemId && apiItem.agendaId && li.agendaItemId === apiItem.agendaId)
            );
            return {
              ...localItem,
              id: apiItem.agendaId,
              agendaItemId: apiItem.agendaId,
              order: localItem?.order || apiItem.order || apiItem.Order || 0, // Preserve order property
              subject: apiItem.subject || localItem?.subject || '',
              agendaTitleEN: apiItem.agendaTitleEN || localItem?.agendaTitleEN || '',
              agendaTitleAR: apiItem.agendaTitleAR || localItem?.agendaTitleAR || ''
            };
          })
        : localAgendaItems;
      
      // Sort agenda items by Order (ascending - Item #1 first, Item #2 second, etc.)
      // Order property comes from the MeetingAgendaItems table
      const agendaItems = [...allAgendaItems].sort((a: any, b: any) => {
        const orderA = a.order || a.Order || 999;
        const orderB = b.order || b.Order || 999;
        return orderA - orderB;
      });
      
      console.log('Building agenda items with decisions:', {
        apiAgendaItems: apiAgendaItems,
        localAgendaItems: localAgendaItems,
        allAgendaItems: allAgendaItems,
        decisionsData: decisionsData,
        decisions: decisionsData?.decisions,
        responses: decisionsData?.responses
      });
      
      this.agendaItemsWithDecisions = agendaItems.map((agendaItem: any, index: number) => {
        // Get agendaId - prioritize id, then agendaItemId, then agendaId property
        const agendaId = agendaItem.id || agendaItem.agendaItemId || agendaItem.agendaId || 0;
        
        console.log('Processing agenda item:', {
          agendaItem: agendaItem,
          agendaId: agendaId
        });
        
        // Find decision for this agenda item - match by agendaId (exact match)
        const decision = decisionsData?.decisions?.find((d: any) => {
          const match = d.agendaId === agendaId;
          if (match) {
            console.log('Found matching decision:', d);
          }
          return match;
        });
        
        // Find responses for this agenda item - match by agendaId (exact match)
        const itemResponses = decisionsData?.responses?.filter((r: any) => {
          const match = r.agendaId === agendaId;
          if (match) {
            console.log('Found matching response:', r);
          }
          return match;
        }) || [];
        
        console.log('Found decision and responses for agendaId', agendaId, ':', {
          decision: decision,
          itemResponses: itemResponses
        });
        
        // Count votes - handle different response formats (accept, accepted, reject, rejected, defer, deferred)
        const acceptedCount = itemResponses.filter((r: any) => {
          const resp = r.response?.toLowerCase() || '';
          return resp === 'accepted' || resp === 'accept';
        }).length;
        const rejectedCount = itemResponses.filter((r: any) => {
          const resp = r.response?.toLowerCase() || '';
          return resp === 'rejected' || resp === 'reject';
        }).length;
        const deferredCount = itemResponses.filter((r: any) => {
          const resp = r.response?.toLowerCase() || '';
          return resp === 'deferred' || resp === 'defer';
        }).length;

        // Get subject from agenda item
        const subject = agendaItem.subject || agendaItem.agendaTitleEN || agendaItem.agendaTitleAR || '';

        return {
          subject: subject,
          order: agendaItem.order || agendaItem.Order || (index + 1), // Include order property
          englishText: 'The Tender Committee reviewed the attached documents.',
          arabicText: 'اطلعت اللجنة على المستندات المرفقة',
          decisionEnglish: decision?.tcDecisionEN || '',
          decisionArabic: decision?.tcDecisionAR || '',
          acceptedVotes: acceptedCount,
          rejectedVotes: rejectedCount,
          deferredVotes: deferredCount,
          totalVotes: itemResponses.length
        };
      }).filter((item: any) => {
        // Show items that have decisions OR responses
        return (item.decisionEnglish || item.decisionArabic) || item.totalVotes > 0;
      });
      
      console.log('Agenda items with decisions built:', this.agendaItemsWithDecisions);

      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error building agenda items with decisions:', error);
      this.agendaItemsWithDecisions = [];
    }
  }

  private calculateAgendaItemTime(meetingStart: Date, index: number): string {
    const itemTime = new Date(meetingStart);
    itemTime.setMinutes(itemTime.getMinutes() + (index * 30)); // 30 minutes per item
    return itemTime.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }


  // ==================== Navigation ====================
  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}`]);
  }

  // ==================== Meeting Actions ====================
  editMeeting(): void {
    // Rule: Only Secretary can edit meetings
    if (!this.canStartMeeting()) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Edit Meeting',
          message: 'Only Secretary can edit meetings.',
          type: 'error'
        }
      });
      return;
    }
    
    // Check if meeting status is Draft - only allow editing Draft meetings
    const status = this.meetingDetails?.status || this.meetingDetails?.statusName || '';
    const statusLower = status.toString().toLowerCase();
    const isDraft = statusLower === 'draft' || statusLower === '1' || status === 1;
    
    if (!isDraft) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Edit Meeting',
          message: 'Only meetings with Draft status can be edited.',
          type: 'error'
        }
      });
      return;
    }
    
    // Get the meeting ID - backend expects MeetingId (string like "M-001"), not integer id
    // Priority: meetingId (string) > meetingDetails.meetingId > meetingDetails.id (fallback to integer if string not available)
    const meetingId = this.meetingId || this.meetingDetails?.meetingId || this.meetingDetails?.id?.toString();
    
    if (!meetingId) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: 'Meeting ID is missing. Cannot edit meeting.',
          type: 'error'
        }
      });
      return;
    }
    
    // Navigate to create-meeting page with meeting ID for editing
    // Convert to string to ensure we're passing the MeetingId (string) not integer id
    this._router.navigate(
      [`/${AppConstants.ROUTES.MEETINGS}/${AppConstants.ROUTES.CREATE_MEETING}`],
      { queryParams: { id: meetingId.toString(), edit: true } }
    );
  }

  normalizeMeetingStatus(status: string | number | null | undefined): string {
    const raw = String(status ?? '').trim().toLowerCase();
    const compact = raw.replace(/\s+/g, '');
    if (raw === '1' || compact === 'draft') {
      return 'draft';
    }
    if (raw === '6' || compact === 'scheduled') {
      return 'scheduled';
    }
    if (raw === '2' || compact === 'waitingforattendance') {
      return 'waitingforattendance';
    }
    if (raw === '3' || compact === 'ongoing') {
      return 'ongoing';
    }
    if (raw === '4' || compact === 'cancelled') {
      return 'cancelled';
    }
    if (raw === '5' || compact === 'completed') {
      return 'completed';
    }
    return compact;
  }
  
  private getCurrentMeetingStatus(): string {
    return this.normalizeMeetingStatus(this.meetingDetails?.status ?? this.meetingDetails?.statusName);
  }

  canShowJoinMeetingButton(): boolean {
    const status = this.getCurrentMeetingStatus();
    return status === 'waitingforattendance' || status === 'ongoing';
  }

  canStartMeeting(): boolean {
    // Rule: Only Secretary can start the meeting,
    // and only the Secretary who created this meeting
    console.log('Checking if user can start meeting:', {
      user: this.userService.user,
      meetingDetails: this.meetingDetails
    });
    let userRole: any = this.userService.user?.Role;
    const currentUserName = (this.userService.user?.FullName || '').toString().trim().toLowerCase();
    const meetingCreatorName = (this.meetingDetails?.createdBy || '').toString().trim().toLowerCase();
    
    // Fallback: Try to read role directly from token if not in user object
    if (!userRole && this.userService.token) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(this.userService.token);
        userRole = decodedToken?.['Role'] || decodedToken?.['role'] || decodedToken?.[AppConstants.CLAIMS.Role];
      } catch (e) {
        console.warn('Could not decode token for role:', e);
      }
    }

    // Demo mode uses the prefilled Admin account. Give that account host
    // privileges so opening the ongoing mock meeting launches the full
    // admin-meeting dialog instead of the attendee dialog.
    if (environment.DISABLE_BACKEND && String(userRole || '').toLowerCase() === 'admin') {
      return true;
    }

    // Check if user is Secretary
    // Handle both string and numeric role values
    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const isSecretary = userRole === 'Secretary' || userRole === 'secretary' || userRole === 'SECRETARY' || 
                       userRole === 'Tender Committee Secretary' || roleValue === 4;

    // If not Secretary, cannot start meeting
    if (!isSecretary) {
      return false;
    }

    // If meeting details not yet loaded, fall back to role-only check
    if (!this.meetingDetails || !meetingCreatorName) {
      return isSecretary;
    }

    // Only the Secretary who created the meeting can start it
    // Comparison is case-insensitive and trimmed to be resilient to formatting differences
    const isCreatorSecretary = currentUserName.length > 0 && currentUserName === meetingCreatorName;
    
    return isSecretary && isCreatorSecretary;
  }

  async cancelMeeting(): Promise<void> {
    if (!this.meetingId || !this.meetingDetails) {
      console.error('Cannot cancel meeting: Missing meeting ID or details');
      return;
    }

    // Rule: Only Secretary can cancel meetings
    if (!this.canStartMeeting()) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Cancel Meeting',
          message: 'Only Secretary can cancel meetings.',
          type: 'error'
        }
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Cancel Meeting',
        message: 'Are you sure you want to cancel this meeting?',
        confirmText: 'Yes, Cancel',
        cancelText: 'No'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/Meetings/${this.meetingDetails.id}/cancel`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess) {
        // Refresh meeting details to get updated status
        await this.fetchMeetingDetails();
        this.dialog.open(MessageDialogComponent, {
          width: '400px',
          data: {
            title: 'Success',
            message: 'Meeting has been cancelled successfully.',
            type: 'success'
          }
        });
      } else {
        console.error('Failed to cancel meeting:', response?.errorMessageEn);
        this.dialog.open(MessageDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: response?.errorMessageEn || 'Failed to cancel meeting. Please try again.',
            type: 'error'
          }
        });
      }
    } catch (error: any) {
      console.error('Error cancelling meeting:', error);
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: error?.error?.errorMessageEn || 'An error occurred while cancelling the meeting. Please try again.',
          type: 'error'
        }
      });
    } finally {
      this._loaderService.hideLoader();
    }
  }

  joinMeeting(demoRole?: 'admin' | 'member'): void {
    if (!this.meetingDetails) {
      console.error('Cannot join meeting: Missing meeting details');
      return;
    }

    // Remove from minimized list since user is explicitly joining
    this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);

    // Rule: Only open dialog if meeting is OnGoing or WaitingForAttendance
    if (this.meetingDetails.status !== 'OnGoing' && this.meetingDetails.status !== 'WaitingForAttendance') {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Join Meeting',
          message: `Meeting is not currently in progress. Current status: ${this.meetingDetails.status}`,
          type: 'error'
        }
      });
      return;
    }

    // Get user role and name
    let userRole = this.userService.user?.Role;
    const userName = this.userService.user?.FullName || '';
    
    // Fallback: Try to read role directly from token if not in user object
    if (!userRole && this.userService.token) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(this.userService.token);
        userRole = decodedToken?.['Role'] || decodedToken?.['role'] || decodedToken?.[AppConstants.CLAIMS.Role];
      } catch (e) {
        console.warn('Could not decode token for role:', e);
      }
    }

    // Check if user is the hoster (Secretary who created the meeting AND is invited)
    const isHoster = demoRole === 'admin' || (demoRole !== 'member' && this.canStartMeeting());

    // Check status (can be string or number)
    const status = this.meetingDetails.status;
    const isWaitingForAttendance = status === 'WaitingForAttendance' || status === 2 || status === '2';
    const isOnGoing = status === 'OnGoing' || status === 3 || status === '3';

    console.log('joinMeeting - User check:', {
      userRole,
      userName,
      isHoster,
      status,
      isWaitingForAttendance,
      isOnGoing
    });

    // Rule: If WaitingForAttendance meeting and user is invited -> show waiting-attendance-dialog
    if (isWaitingForAttendance && this.isUserInvited()) {
      const meetingTitle = this.meetingDetails?.title || 'Meeting';
      console.log('Opening waiting-attendance-dialog for WaitingForAttendance meeting');
      this.openWaitingAttendanceDialog(meetingTitle);
      return;
    } else if (isWaitingForAttendance && !this.isUserInvited()) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Join Meeting',
          message: 'You are not invited to this meeting.',
          type: 'error'
        }
      });
      return;
    }

    // Rule: If OnGoing meeting and user is hoster AND invited -> show admin-meeting-dialog
    if (isHoster && isOnGoing && this.isUserInvited()) {
      const agendaItems = this.meetingDetails?.agendaItems || [];
      const meetingTitle = this.meetingDetails?.title || 'Meeting';
      const meetingData = {
        agendaItems: agendaItems,
        meetingId: this.meetingId
      };
      console.log('Opening admin-meeting-dialog for hoster who is invited');
      this.openAdminMeetingDialog(meetingTitle, meetingData);
      return;
    }

    // Rule: If WaitingForAttendance or OnGoing meeting and user is invited -> show join-meeting-dialog
    // Allow any invited member to join (not just TCM)
    if (!isHoster && this.isUserInvited() && (isOnGoing || isWaitingForAttendance)) {
      if (this.hasOpenMeetingDialog(['join', 'admin'])) {
        console.log('Meeting dialog is already open for this meeting - skipping manual join open');
        this.isDialogOpen = true;
        return;
      }

      console.log('Opening join-meeting-dialog for invited member');
      const agendaItems = this.meetingDetails?.agendaItems || [];
      const dialogRef = this.dialog.open(JoinMeetingDialogComponent, {
        width: '1100px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        disableClose: false,
        panelClass: 'join-meeting-dialog',
        data: {
          meetingTitle: this.meetingDetails?.title || 'Meeting',
          agendaItems: agendaItems,
          currentAgendaIndex: this.currentAgendaIndex || 0, // Use current agenda index where hoster is
          meetingId: this.meetingId
        }
      });

      this.meetingSessionDialogRef = dialogRef;
      this.isDialogOpen = true; // Set flag when dialog opens

      dialogRef.afterClosed().subscribe(result => {
        // Always enable button when dialog closes (handles all close scenarios: outside click, back button, ended, left, etc.)
        this.isDialogOpen = false;
        this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
        
        // If minimized, return early (button already enabled above)
        if (result?.action === 'minimized') {
          return;
        }
        
        // If meeting was ended, don't re-add to minimized list
        if (result?.action === 'ended' || result?.action === 'completed') {
          // Meeting was ended, ensure it's removed from minimized list
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
          this.meetingSessionDialogRef = null;
          this.isRestoringDialog = false;
          // Refresh meeting details to get updated status
          this.fetchMeetingDetails();
          return;
        }
        
        // If actually closed (not minimized), check if meeting is still active
        if (result !== 'minimized' && result?.action !== 'minimized') {
          // Check if meeting is still active (OnGoing or WaitingForAttendance)
          // Also check if it's NOT completed or cancelled
          const isCompleted = this.meetingDetails?.status === 'Completed' || 
                            this.meetingDetails?.status === 5 ||
                            this.meetingDetails?.status === '5';
          const isCancelled = this.meetingDetails?.status === 'Cancelled' || 
                           this.meetingDetails?.status === 4 ||
                           this.meetingDetails?.status === '4';
          
          if (isCompleted || isCancelled) {
            // Meeting is completed or cancelled, remove from minimized list
            this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
            return;
          }
          
          const isActive = this.meetingDetails?.status === 'OnGoing' || 
                         this.meetingDetails?.status === 'WaitingForAttendance' ||
                         this.meetingDetails?.status === 3 || 
                         this.meetingDetails?.status === 2;
          
          if (isActive) {
            // Meeting is still active, add back to minimized list (user clicked outside)
            const meetingTitle = this.meetingDetails?.title || 'Meeting';
            const agendaItems = this.meetingDetails?.agendaItems || [];
            this.minimizedMeetingService.addMinimizedMeeting({
              meetingId: this.meetingId,
              meetingTitle: meetingTitle,
              status: this.meetingDetails?.status?.toString() || 'OnGoing',
              dialogType: 'join',
              dialogData: {
                meetingTitle: meetingTitle,
                agendaItems: agendaItems,
                currentAgendaIndex: this.currentAgendaIndex || 0, // Use current agenda index
                meetingId: this.meetingId,
                meetingStatus: this.meetingDetails?.status
              }
            });
          } else {
            // Meeting is not active, remove from minimized list
            this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
          }
        }
        if (result) {
          this.handleJoinMeetingResult(result);
        }
      });
      return;
    }

    // If not hoster and not invited, show error
    if (!this.isUserInvited()) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Join Meeting',
          message: 'You are not invited to this meeting.',
          type: 'error'
        }
      });
    }
  }

  async startMeeting(): Promise<void> {
    if (!this.meetingId || !this.meetingDetails) {
      console.error('Cannot start meeting: Missing meeting ID or details');
      return;
    }

    // Rule: Only Secretary can start the meeting
    if (!this.canStartMeeting()) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Start Meeting',
          message: 'Only Secretary can start the meeting.',
          type: 'error'
        }
      });
      return;
    }

    // Rule: Meeting must be published (Scheduled) before it can be started
    if (this.meetingDetails.status !== 'Scheduled') {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Start Meeting',
          message: `Meeting must be published (Scheduled) before it can be started. Current status: ${this.meetingDetails.status}`,
          type: 'error'
        }
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Start Meeting',
        message: 'Are you sure you want to start this meeting?',
        confirmText: 'Start',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        this._loaderService.showLoader();
        try {
          // Call backend API to start the meeting (changes status to WaitingForAttendance)
          const url = `${environment.API_URL}/api/Meetings/${this.meetingDetails.id}/start`;
          const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

          if (response?.isSuccess) {
            // Refresh meeting details to get updated status (should be WaitingForAttendance now)
            await this.fetchMeetingDetails();

            // Prepare meeting data with status included
            const meetingTitle = this.meetingDetails.title || 'Meeting';
            const agendaItems = this.meetingDetails.agendaItems || [];
            const meetingStatus = this.meetingDetails.status || 'WaitingForAttendance';
            const meetingData = {
              agendaItems: agendaItems,
              meetingId: this.meetingId,
              status: meetingStatus,
              members: this.meetingDetails.members || []
            };

            // Start the meeting session via SignalR (this will notify all connected users and trigger dialog)
            // The SignalR event will automatically open the waiting attendance dialog via openMeetingSession
            await this.signalRService.startMeetingSession(
              this.meetingId,
              meetingTitle,
              meetingData
            );
          } else {
            console.error('Failed to start meeting:', response?.errorMessageEn);
            this.dialog.open(MessageDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: response?.errorMessageEn || 'Failed to start meeting. Please try again.',
                type: 'error'
              }
            });
          }
        } catch (error: any) {
          console.error('Error starting meeting:', error);
          this.dialog.open(MessageDialogComponent, {
            width: '400px',
            data: {
              title: 'Error',
              message: error?.error?.errorMessageEn || 'An error occurred while starting the meeting. Please try again.',
              type: 'error'
            }
          });
        } finally {
          this._loaderService.hideLoader();
        }
      }
    });
  }

  async publishMeeting(): Promise<void> {
    if (!this.meetingId || !this.meetingDetails) {
      console.error('Cannot publish meeting: Missing meeting ID or details');
      return;
    }

    // Rule: Only Secretary can publish meetings (any secretary, not only hoster)
    if (!this.isSecretary()) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Cannot Publish Meeting',
          message: 'Only Secretary can publish meetings.',
          type: 'error'
        }
      });
      return;
    }

    // Check if this will be an emergency meeting (for warning only - validation is handled by backend)
    // Emergency if published on meeting date (0 days) or meeting date + 1 day (1 day)
    // NOT emergency if published on meeting date + 2 days (2 days)
    const meetingDateTime = new Date(this.meetingDetails.dateTime);
    const now = new Date();
    
    // Get dates only (no time) for comparison
    const meetingDate = new Date(meetingDateTime.getFullYear(), meetingDateTime.getMonth(), meetingDateTime.getDate());
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate days difference: positive means current date is after meeting date
    const daysDifference = Math.floor((currentDate.getTime() - meetingDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Will be emergency if published before day 6 (i.e., on meeting date or meeting date + 1)
    const willBeEmergencyMeeting = daysDifference >= 0 && daysDifference < 2;

    let dialogTitle = 'Publish Meeting';
    let dialogMessage = 'Are you sure you want to publish this meeting? This will change its status to Scheduled.';
    
    if (willBeEmergencyMeeting) {
      dialogTitle = '⚠️ Emergency Meeting Warning';
      dialogMessage = `⚠️ WARNING: This meeting will be published as an EMERGENCY MEETING!\n\n` +
                     `Meeting date: ${meetingDate.toLocaleDateString()}\n` +
                     `Current date: ${currentDate.toLocaleDateString()}\n` +
                     `Days difference: ${daysDifference} day(s)\n\n` +
                     `Emergency meetings are those published on the meeting date or the day after. Are you sure you want to continue?`;
    }

    // Validation is handled by backend - just show confirmation dialog with warning if emergency
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: willBeEmergencyMeeting ? '500px' : '400px',
      data: {
        title: dialogTitle,
        message: dialogMessage,
        confirmText: 'Yes, Publish',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/Meetings/${this.meetingDetails.id}/publish`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess) {
        // Refresh meeting details to get updated status
        await this.fetchMeetingDetails();
        this.dialog.open(MessageDialogComponent, {
          width: '400px',
          data: {
            title: 'Success',
            message: 'Meeting has been published successfully.',
            type: 'success'
          }
        });
      } else {
        console.error('Failed to publish meeting:', response?.errorMessageEn);
        this.dialog.open(MessageDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: response?.errorMessageEn || 'Failed to publish meeting. Please try again.',
            type: 'error'
          }
        });
      }
    } catch (error: any) {
      console.error('Error publishing meeting:', error);
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: error?.error?.errorMessageEn || 'An error occurred while publishing the meeting. Please try again.',
          type: 'error'
        }
      });
    } finally {
      this._loaderService.hideLoader();
    }
  }

  recordMinutes(): void {
    // TODO: Implement record minutes functionality
    console.log('Record minutes for meeting:', this.meetingId);
  }

  // ==================== Admin Meeting Dialog ====================
  openAdminMeetingDialog(meetingTitle: string, meetingData: any): void {
    if (this.hasOpenMeetingDialog(['admin', 'join'])) {
      console.log('Meeting dialog is already open for this meeting - skipping admin dialog');
      this.isDialogOpen = true;
      this.dialogOpenLock = false;
      return;
    }

    // Check lock first - if locked, skip
    if (this.dialogOpenLock) {
      console.log('Dialog opening already in progress (lock active) - skipping admin dialog');
      return;
    }
    
    // Check if dialog is already open using flag
    if (this.isDialogOpen) {
      console.log('Admin meeting dialog already open (flag check) - skipping');
      return;
    }

    // Check if dialog is actually open by checking overlay container
    let isDialogActuallyOpen = false;
    if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
      try {
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
        isDialogActuallyOpen = !!dialogElement;
      } catch (e) {
        isDialogActuallyOpen = true;
      }
    }
    
    // Prevent opening multiple dialogs only if dialog is actually open
    if (isDialogActuallyOpen) {
      console.log('Admin meeting dialog already open (DOM check) - skipping');
      this.isDialogOpen = true; // Sync the flag
      return;
    }
    
    // Clear stale reference if exists
    if (this.meetingSessionDialogRef && !isDialogActuallyOpen) {
      console.log('Clearing stale admin dialog reference');
      this.meetingSessionDialogRef = null;
    }

    // Acquire lock
    this.dialogOpenLock = true;

    console.log('Opening admin meeting dialog with data:', {
      meetingTitle,
      meetingId: this.meetingId,
      agendaItemsCount: meetingData?.agendaItems?.length || 0
    });

    // Remove from minimized list when opening dialog (toaster should be hidden when dialog is open)
    this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);

    // Prioritize agenda items from meetingDetails (which have TC Decisions from API)
    // Fall back to meetingData if meetingDetails is not available
    const agendaItemsToPass = this.meetingDetails?.agendaItems || meetingData?.agendaItems || [];
    
    // Note: Current agenda index will be restored from backend session via MeetingSessionState
    // when the dialog calls joinMeeting in ngOnInit
    
    console.log('Opening admin dialog with agenda items:', {
      agendaItemsCount: agendaItemsToPass.length,
      firstItem: agendaItemsToPass[0],
      hasTCDecisions: agendaItemsToPass.some((ai: any) => ai.tcDecisionEN || ai.tcDecisionAR),
      currentAgendaIndex: this.currentAgendaIndex || 0
    });

    const dialogRef = this.dialog.open(AdminMeetingDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'admin-meeting-dialog',
      data: {
        ...(meetingData || {}),
        meetingTitle: meetingTitle,
        meetingId: this.meetingId,
        meetingIdInt: this.meetingDetails?.id, // Pass integer ID for API calls
        id: this.meetingDetails?.id, // Also pass as 'id' for compatibility
        agendaItems: agendaItemsToPass, // Use agenda items with TC Decisions
        currentAgendaIndex: meetingData?.currentAgendaIndex ?? this.currentAgendaIndex ?? 0,
        meetingStatus: this.meetingDetails?.status, // Pass meeting status for minimize functionality
        meetingMembers: this.meetingDetails?.members || meetingData?.meetingMembers || []
      }
    });

    this.meetingSessionDialogRef = dialogRef;
    this.isDialogOpen = true; // Set flag when dialog opens
    this.dialogOpenLock = false; // Release lock after setting flags

      dialogRef.afterClosed().subscribe(result => {
      console.log('Admin meeting dialog closed', result);
      
      // Always enable button when dialog closes (handles all close scenarios: outside click, back button, ended, etc.)
      this.isDialogOpen = false;
      this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
      
      // If minimized, keep reference for restoration and return early
      if (result?.action === 'minimized') {
        // Dialog is minimized, keep reference for restoration
        return;
      }
      
      // If meeting was ended, refresh meeting details immediately to update status
      if (result?.action === 'ended') {
        this.fetchMeetingDetails();
        // Remove from minimized list (already done in endMeeting, but ensure it's removed)
        this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
        this.meetingSessionDialogRef = null;
        this.isRestoringDialog = false;
        return;
      }
      
      // If actually closed (not minimized), check if meeting is still active
      if (result !== 'minimized' && result?.action !== 'minimized') {
        // Check if meeting is still active (OnGoing or WaitingForAttendance)
        const isActive = this.meetingDetails?.status === 'OnGoing' || 
                       this.meetingDetails?.status === 'WaitingForAttendance' ||
                       this.meetingDetails?.status === 3 || 
                       this.meetingDetails?.status === 2;
        
        if (isActive) {
          // Meeting is still active, add back to minimized list (user clicked outside)
          const meetingTitle = this.meetingDetails?.title || 'Meeting';
          const agendaItems = this.meetingDetails?.agendaItems || [];
          this.minimizedMeetingService.addMinimizedMeeting({
            meetingId: this.meetingId,
            meetingTitle: meetingTitle,
            status: this.meetingDetails?.status?.toString() || 'OnGoing',
            dialogType: 'admin',
            dialogData: {
              meetingTitle: meetingTitle,
              meetingId: this.meetingId,
              meetingIdInt: this.meetingDetails?.id,
              id: this.meetingDetails?.id,
              agendaItems: agendaItems,
              currentAgendaIndex: this.currentAgendaIndex || 0, // Use current agenda index where hoster is
              meetingStatus: this.meetingDetails?.status
            }
          });
        } else {
          // Meeting is not active, remove from minimized list
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
        }
      }
      this.meetingSessionDialogRef = null; // Reset when dialog is closed
      this.isRestoringDialog = false; // Ensure flag is reset when dialog closes
      // Refresh meeting details if meeting was begun
      if (result?.action === 'begun') {
        this.fetchMeetingDetails();
      }
    });
  }

  // ==================== Waiting Attendance Dialog ====================
  openWaitingAttendanceDialog(meetingTitle: string): void {
    if (this.hasOpenMeetingDialog(['waiting', 'admin', 'join'])) {
      console.log('Meeting dialog is already open for this meeting - skipping waiting attendance dialog');
      this.isDialogOpen = true;
      return;
    }

    // Check if dialog is already open using flag
    if (this.isDialogOpen) {
      console.log('Waiting attendance dialog already open (flag check) - skipping');
      return;
    }

    // Check if dialog is actually open by checking overlay container
    let isDialogActuallyOpen = false;
    if (this.meetingSessionDialogRef && this.meetingSessionDialogRef.componentInstance) {
      try {
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
        isDialogActuallyOpen = !!dialogElement;
      } catch (e) {
        isDialogActuallyOpen = true;
      }
    }
    
    // Prevent opening multiple dialogs only if dialog is actually open
    if (isDialogActuallyOpen) {
      console.log('Waiting attendance dialog already open (DOM check) - skipping');
      this.isDialogOpen = true; // Sync the flag
      return;
    }
    
    // Clear stale reference if exists
    if (this.meetingSessionDialogRef && !isDialogActuallyOpen) {
      console.log('Clearing stale waiting attendance dialog reference');
      this.meetingSessionDialogRef = null;
    }

    console.log('Opening waiting attendance dialog with data:', {
      meetingTitle,
      meetingId: this.meetingId,
      membersCount: this.meetingDetails?.members?.length || 0
    });

    // Remove from minimized list BEFORE opening dialog (toaster should be hidden when dialog is open)
    this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
    // Also remove by all possible ID formats to be sure
    if (this.meetingDetails?.id) {
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingDetails.id.toString());
    }
    if (this.meetingDetails?.meetingId) {
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingDetails.meetingId);
    }

    const dialogRef = this.dialog.open(WaitingAttendanceDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'waiting-attendance-dialog',
      data: {
        meetingTitle: meetingTitle,
        meetingId: this.meetingId,
        meetingIdInt: this.meetingDetails?.id, // Pass integer ID for API calls
        id: this.meetingDetails?.id, // Also pass as 'id' for compatibility
        members: this.meetingDetails?.members || [], // Pass invited members
        agendaItems: this.meetingDetails?.agendaItems || [] // Pass agenda items
      }
    });

    this.meetingSessionDialogRef = dialogRef;
    this.isDialogOpen = true; // Set flag when dialog opens
    // Reset the processing flag since dialog is now open
    this.isProcessingMeetingSession = false;
    
    // Remove again after dialog opens (in case it was added back)
    setTimeout(() => {
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
      if (this.meetingDetails?.id) {
        this.minimizedMeetingService.removeMinimizedMeeting(this.meetingDetails.id.toString());
      }
      if (this.meetingDetails?.meetingId) {
        this.minimizedMeetingService.removeMinimizedMeeting(this.meetingDetails.meetingId);
      }
    }, 50);

    dialogRef.afterClosed().subscribe(async result => {
      console.log('Waiting attendance dialog closed', result);
      
      // Always enable button when dialog closes (handles all close scenarios)
      this.isDialogOpen = false;
      this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
      
      // Reset processing flag when dialog closes
      this.isProcessingMeetingSession = false;
      
      // If meeting was ended, refresh details and don't restore
      if (result?.action === 'ended') {
        await this.fetchMeetingDetails();
        // Ensure it's removed from minimized list
        this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
        this.minimizedMeetingService.removeMeetingsByStatus(['Cancelled', '4', 'cancelled']);
        this.meetingSessionDialogRef = null;
        this.isRestoringDialog = false;
        return;
      }
      
      // If minimized, return early (button already enabled above)
      if (result?.action === 'minimized') {
        // Dialog is minimized, keep reference for restoration
        return;
      }
      
      // Refresh meeting details to get latest status before checking
      await this.fetchMeetingDetails();
      
      // If actually closed (not minimized), check if meeting is still active
      if (result !== 'minimized' && result?.action !== 'minimized') {
        // Check if meeting is still active (OnGoing or WaitingForAttendance)
        // Also check that it's NOT cancelled or completed
        const status = this.meetingDetails?.status;
        const isCancelled = status === 'Cancelled' || status === 4 || status === '4';
        const isCompleted = status === 'Completed' || status === 5 || status === '5';
        const isActive = (status === 'OnGoing' || status === 'WaitingForAttendance' ||
                         status === 3 || status === 2 || status === '3' || status === '2') &&
                         !isCancelled && !isCompleted;
        
        if (isActive) {
          // Meeting is still active, add back to minimized list (user clicked outside)
          const meetingTitle = this.meetingDetails?.title || 'Meeting';
          this.minimizedMeetingService.addMinimizedMeeting({
            meetingId: this.meetingId,
            meetingTitle: meetingTitle,
            status: this.meetingDetails?.status?.toString() || 'WaitingForAttendance',
            dialogType: 'waiting',
            dialogData: {
              meetingTitle: meetingTitle,
              meetingId: this.meetingId,
              meetingIdInt: this.meetingDetails?.id,
              id: this.meetingDetails?.id,
              members: this.meetingDetails?.members || [],
              agendaItems: this.meetingDetails?.agendaItems || []
            }
          });
        } else {
          // Meeting is not active (cancelled/completed), remove from minimized list
          this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
        }
      }
      this.meetingSessionDialogRef = null; // Reset when dialog is closed
      this.isRestoringDialog = false; // Ensure flag is reset when dialog closes
      
      // If meeting was begun, refresh details and open the admin dialog for hoster
      if (result?.action === 'begun') {
        await this.fetchMeetingDetails();
        
        // Auto-open the appropriate dialog for the user
        // Check if user is hoster AND invited
        const isHoster = this.canStartMeeting();
        
        if (isHoster && this.isUserInvited()) {
          // Open admin dialog for hoster who is invited after a short delay to ensure state is updated
          setTimeout(() => {
            // CRITICAL: Reset flags BEFORE trying to open the admin dialog
            // Otherwise the duplicate prevention checks will block it
            this.isDialogOpen = false;
            this.dialogOpenLock = false;
            this.meetingSessionDialogRef = null;
            
            const meetingTitle = this.meetingDetails?.title || 'Meeting';
            const agendaItems = this.meetingDetails?.agendaItems || [];
            const meetingData = {
              agendaItems: agendaItems,
              meetingId: this.meetingId,
              status: 'OnGoing',
              members: this.meetingDetails?.members || []
            };
            
            console.log('Opening admin dialog for hoster who is invited after "Begin Meeting" was clicked');
            this.openAdminMeetingDialog(meetingTitle, meetingData);
          }, 300);
        } else if (this.isUserInvited()) {
          // Open join dialog for invited members after a longer delay to ensure meeting status is updated
          setTimeout(() => {
            // CRITICAL: Reset flags BEFORE trying to open the join dialog
            // Otherwise the duplicate prevention checks will block it
            console.log('Before reset - isDialogOpen:', this.isDialogOpen, 'dialogOpenLock:', this.dialogOpenLock, 'meetingSessionDialogRef:', !!this.meetingSessionDialogRef);
            this.isDialogOpen = false;
            this.dialogOpenLock = false;
            this.meetingSessionDialogRef = null;
            console.log('After reset - isDialogOpen:', this.isDialogOpen, 'dialogOpenLock:', this.dialogOpenLock, 'meetingSessionDialogRef:', !!this.meetingSessionDialogRef);
            console.log('Meeting status before opening join dialog:', this.meetingDetails?.status);
            
            const meetingTitle = this.meetingDetails?.title || 'Meeting';
            const agendaItems = this.meetingDetails?.agendaItems || [];
            const meetingData = {
              agendaItems: agendaItems,
              meetingId: this.meetingId,
              status: 'OnGoing',
              members: this.meetingDetails?.members || []
            };
            
            console.log('Calling openMeetingSession for join dialog after "Begin Meeting" was clicked');
            this.openMeetingSession({
              meetingId: this.meetingId,
              meetingTitle: meetingTitle,
              meetingData: meetingData
            });
          }, 500);
        }
      }
    });
  }

  // ==================== Attachments Dialog ====================
  isLoadingAttachments: boolean = false;
  
  async openAttachmentsDialog(item: AgendaItem): Promise<void> {
    this.currentAgendaItemSubject = item.subject || '';
    this.showAttachmentsDialog = true;
    this.currentAttachments = [];
    this.cdr.markForCheck();
    
    // Load attachments from backend
    await this.loadAttachmentsForAgendaItem(item);
  }

  closeAttachmentsDialog(): void {
    this.showAttachmentsDialog = false;
    this.currentAttachments = [];
    this.currentAgendaItemSubject = '';
  }

  openAgendaItemDetails(item: AgendaItem): void {
    // Get agenda item ID (could be id or agendaItemId)
    const agendaItemId = item['id'] || item['agendaItemId'];
    if (!agendaItemId) {
      console.error('Agenda item ID not found');
      return;
    }

    // Open the view agenda item details dialog (separate from create/edit dialog)
    this.dialog.open(ViewAgendaItemDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'view-agenda-item-dialog',
      data: {
        agendaItemId: agendaItemId,
        agendaItem: item // Pass the item data directly as fallback
      }
    });
  }

  isSecretary(): boolean {
    // Use the same logic as canStartMeeting()
    let userRole: any = this.userService.user?.Role;
    
    // Fallback: Try to read role directly from token if not in user object
    if (!userRole && this.userService.token) {
      try {
        const token = this.userService.token;
        const payload = JSON.parse(atob(token.split('.')[1]));
        userRole = payload?.['Role'] || payload?.['role'] || payload?.[AppConstants.CLAIMS.Role];
      } catch (e) {
        console.warn('Could not decode token for role:', e);
      }
    }
    
    // Check if user is Secretary
    // Handle both string and numeric role values
    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const isSecretary = userRole === 'Secretary' || userRole === 'secretary' || userRole === 'SECRETARY' || 
                       userRole === 'Tender Committee Secretary' || roleValue === 4;
    
    return isSecretary;
  }

  openEditAgendaItem(item: AgendaItem): void {
    // Get agenda item ID (could be id or agendaItemId)
    const agendaItemId = item['id'] || item['agendaItemId'];
    if (!agendaItemId) {
      console.error('Agenda item ID not found for editing.');
      return;
    }

    // Open the create/edit agenda item dialog in edit mode
    this.dialog.open(CreateAgendaItemDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'create-agenda-item-dialog',
      data: {
        editMode: true,
        agendaItemId: agendaItemId,
        meetingId: this.meetingDetails?.id || null
      }
    });
  }

  async loadAttachmentsForAgendaItem(item: AgendaItem): Promise<void> {
    // Get agenda item ID (could be id or agendaItemId)
    const agendaItemId = item['id'] || item['agendaItemId'];
    if (!agendaItemId) {
      this.currentAttachments = [];
      this.isLoadingAttachments = false;
      this.cdr.markForCheck();
      return;
    }

    this.isLoadingAttachments = true;
    this.currentAttachments = [];
    this.cdr.markForCheck();

    try {
      // Fetch documents for this agenda item
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: agendaItemId.toString(),
        processName: 'AgendaItem',
        processID: agendaItemId.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;
          
          // Use direct blob access (SAS URLs)
          const USE_DIRECT_BLOB_ACCESS = true;
          
          // Process documents and fetch file sizes
          const attachmentPromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name;
              } else {
                return doc.attachmentId && doc.name;
              }
            })
            .map(async (doc: any) => {
              // Format file size
              const formatFileSize = (bytes: number): string => {
                if (!bytes || bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
              };

              // Get file URL - use SAS URL if available, otherwise use download endpoint
              let fileUrl: string;
              if (USE_DIRECT_BLOB_ACCESS && doc.fileContent) {
                fileUrl = doc.fileContent; // SAS URL
              } else {
                fileUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
              }

              // Fetch file to get actual size
              let fileSize = 0;
              try {
                const response = await fetch(fileUrl, { method: 'HEAD' });
                const contentLength = response.headers.get('content-length');
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10);
                } else {
                  // If HEAD doesn't work, fetch the blob to get size
                  const blobResponse = await fetch(fileUrl);
                  const blob = await blobResponse.blob();
                  fileSize = blob.size;
                }
              } catch (error) {
                console.warn(`Could not fetch file size for ${doc.name}:`, error);
                // Try to use doc.size if available as fallback
                fileSize = doc.size || doc.fileSize || 0;
              }

              // Clean filename - remove attachment IDs and UUIDs
              let cleanFileName = doc.name || 'document';
              const extension = cleanFileName.includes('.') ? cleanFileName.split('.').pop() || '' : '';
              const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
              const hasUUIDs = uuidPattern.test(cleanFileName);
              
              if (hasUUIDs) {
                cleanFileName = extension ? `document.${extension}` : 'document';
              }

              return {
                name: cleanFileName,
                originalName: doc.name,
                url: fileUrl,
                size: formatFileSize(fileSize),
                fileType: doc.mimeType || 'application/octet-stream',
                uploadedBy: doc.uploadedBy || 'System',
                uploadedDate: doc.uploadedDate || '',
                description: doc.description || '',
                date: doc.date || new Date(),
                attachmentFor: doc.attachmentFor || '',
                fileContent: doc.fileContent || '',
                additionalDetails: doc.additionalDetails || '',
                mimeType: doc.mimeType || 'application/octet-stream'
              } as MeetingAttachment;
            });

          // Wait for all attachments to be processed
          this.currentAttachments = await Promise.all(attachmentPromises);
          this.cdr.markForCheck();
        } else {
          // No attachments found
          this.currentAttachments = [];
          this.cdr.markForCheck();
        }
      } else {
        // No data in response
        this.currentAttachments = [];
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
      this.currentAttachments = [];
    } finally {
      this.isLoadingAttachments = false;
      this.cdr.markForCheck();
    }
  }

  async previewAttachment(attachment: MeetingAttachment): Promise<void> {
    try {
      if (!attachment.url || attachment.url === '#') {
        throw new Error('Invalid attachment URL');
      }

      // Detect Safari/iPad - Safari blocks window.open() after async operations
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                       /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // For Safari/iPad: Open the original URL directly in a new tab (no blank page)
      if (isSafari) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
        return;
      }

      // For Chrome/other browsers: Fetch as blob to prevent downloads
      // Fetch the file as a blob to create an object URL for preview
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const fileType = (attachment as any).fileType || attachment.mimeType || 'application/octet-stream';
      const file = new File([blob], attachment.name, { type: fileType });
      const fileURL = URL.createObjectURL(file);
      
      // Open in new tab with file loaded
      window.open(fileURL, '_blank', 'noopener,noreferrer');
      
      // Don't revoke the blob URL - let browser handle cleanup when tab closes
    } catch (error) {
      console.error('Error previewing attachment:', error);
    }
  }

  async downloadFile(attachment: MeetingAttachment): Promise<void> {
    try {
      if (!attachment.url || attachment.url === '#') {
        throw new Error('Invalid attachment URL');
      }

      // Fetch the file as a blob
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a File object with the clean filename (without IDs)
      const fileType = (attachment as any).fileType || attachment.mimeType || 'application/octet-stream';
      const file = new File([blob], attachment.name, { type: fileType });
      
      // Create object URL and trigger download with clean filename
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = attachment.name; // Use clean filename (without IDs)
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  }

  async loadMeetingAttachments(): Promise<void> {
    if (!this.meetingDetails?.id) {
      this.meetingAttachments = [];
      return;
    }

    this.isLoadingMeetingAttachments = true;
    this.meetingAttachments = [];

    try {
      // Fetch documents for this meeting
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: this.meetingDetails.id.toString(),
        processName: 'Meeting',
        processID: this.meetingDetails.id.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;
          
          // Use direct blob access (SAS URLs)
          const USE_DIRECT_BLOB_ACCESS = true;
          
          // Process documents and fetch file sizes
          const attachmentPromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name;
              } else {
                return doc.attachmentId && doc.name;
              }
            })
            .map(async (doc: any) => {
              // Format file size
              const formatFileSize = (bytes: number): string => {
                if (!bytes || bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
              };

              // Get file URL - use SAS URL if available, otherwise use download endpoint
              let fileUrl: string;
              if (USE_DIRECT_BLOB_ACCESS && doc.fileContent) {
                fileUrl = doc.fileContent; // SAS URL
              } else {
                fileUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
              }

              // Fetch file to get actual size
              let fileSize = 0;
              try {
                const response = await fetch(fileUrl, { method: 'HEAD' });
                const contentLength = response.headers.get('content-length');
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10);
                } else {
                  // If HEAD doesn't work, fetch the blob to get size
                  const blobResponse = await fetch(fileUrl);
                  const blob = await blobResponse.blob();
                  fileSize = blob.size;
                }
              } catch (error) {
                console.warn(`Could not fetch file size for ${doc.name}:`, error);
                // Try to use doc.size if available as fallback
                fileSize = doc.size || doc.fileSize || 0;
              }

              // Clean filename - remove attachment IDs and UUIDs
              let cleanFileName = doc.name || 'document';
              const extension = cleanFileName.includes('.') ? cleanFileName.split('.').pop() || '' : '';
              const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
              const hasUUIDs = uuidPattern.test(cleanFileName);
              
              if (hasUUIDs) {
                cleanFileName = extension ? `document.${extension}` : 'document';
              }

              return {
                name: cleanFileName,
                originalName: doc.name,
                url: fileUrl,
                size: formatFileSize(fileSize),
                fileType: doc.mimeType || 'application/octet-stream',
                uploadedBy: doc.uploadedBy || 'System',
                uploadedDate: doc.uploadedDate || '',
                description: doc.description || '',
                date: doc.date || new Date(),
                attachmentFor: doc.attachmentFor || '',
                fileContent: doc.fileContent || '',
                additionalDetails: doc.additionalDetails || '',
                mimeType: doc.mimeType || 'application/octet-stream'
              } as MeetingAttachment;
            });

          // Wait for all attachments to be processed
          this.meetingAttachments = await Promise.all(attachmentPromises);
        }
      }
    } catch (error) {
      console.error('Error loading meeting attachments:', error);
      this.meetingAttachments = [];
    } finally {
      this.isLoadingMeetingAttachments = false;
    }
  }

  openMeetingDocumentsDialog(): void {
    this.showMeetingDocumentsDialog = true;
    // Ensure attachments are loaded
    if (this.meetingAttachments.length === 0 && !this.isLoadingMeetingAttachments) {
      this.loadMeetingAttachments();
    }
  }

  closeMeetingDocumentsDialog(): void {
    this.showMeetingDocumentsDialog = false;
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'pdf';
    if (extension === 'xlsx' || extension === 'xls') return 'excel';
    if (extension === 'doc' || extension === 'docx') return 'word';
    return 'file';
  }

  // ==================== PDF Export ====================
  async exportMinutesAsPDF(): Promise<void> {
    if (!this.meetingDetails?.meetingMinutesData) {
      console.error('No meeting minutes data available');
      return;
    }

    try {
      const doc = new jsPDF();
      const data = this.meetingDetails.meetingMinutesData;
      const margin = 20;
      const maxWidth = doc.internal.pageSize.getWidth() - (margin * 2);
      const colors = {
        dark: [26, 26, 26],
        gray: [51, 51, 51],
        light: [102, 102, 102],
        purple: [83, 46, 96],
        green: [0, 166, 62]
      };
      
      let logoData: { imgData: string; width: number; height: number } | null = null;
      try {
        logoData = await this.loadLogoImage();
      } catch (error) {
        console.warn('Failed to load logo, continuing without it:', error);
      }

      let y = 10;
      if (logoData) {
        doc.addImage(logoData.imgData, 'PNG', margin, y, logoData.width, logoData.height);
        y += logoData.height + 25;
      } else {
        y = 20;
      }

      const checkPage = () => {
        if (y > doc.internal.pageSize.getHeight() - 20) {
          doc.addPage();
          y = 10;
          if (logoData) {
            doc.addImage(logoData.imgData, 'PNG', margin, y, logoData.width, logoData.height);
            y += logoData.height + 25;
          } else {
            y = 20;
          }
        }
      };

      const addText = (text: string, size: number, bold = false, color = colors.dark, spacing = 5, xOffset = 0, lineHeight = 0) => {
        doc.setFontSize(size);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setTextColor(color[0], color[1], color[2]);
        const lines = doc.splitTextToSize(text, maxWidth - xOffset);
        lines.forEach((line: string) => {
          checkPage();
          doc.text(line, margin + xOffset, y);
          y += lineHeight || size * 0.5;
        });
        y += spacing;
      };

      const addSection = (title: string, content: string | string[], spacing = 10) => {
        addText(title, 16, true);
        Array.isArray(content) ? content.forEach(item => addText(`• ${item}`, 11)) : addText(content, 12, false, colors.gray);
        y += spacing;
      };

      // Header
      addText('Meeting Minutes', 20, true);
      addText(`Recorded by ${data.recordedBy} on ${data.recordedDate}`, 12, false, colors.light, 10);

      // Meeting Summary
      addSection('Meeting Summary', data.summary);

      // Attendance Table
      if (data.attendanceTable?.length > 0) {
        addText('Attendance:', 16, true);
        y += 5;
        
        const tableStartX = margin;
        const colWidths = [15, 50, 40, 20, 20, 30, 50];
        const headerY = y;
        
        doc.setFillColor(colors.purple[0], colors.purple[1], colors.purple[2]);
        doc.rect(tableStartX, headerY - 6, maxWidth, 8, 'F');
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        const headers = ['الرقم', 'اسم العضو', 'الصفة', 'حضور', 'Attendance', 'Role', 'Member Name'];
        let currentX = tableStartX + 2;
        headers.forEach((header, idx) => {
          doc.text(header, currentX, headerY);
          currentX += colWidths[idx];
        });
        
        doc.setTextColor(0, 0, 0);
        y += 10;
        
        data.attendanceTable.forEach((member: any) => {
          checkPage();
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
          
          currentX = tableStartX + 2;
          const rowData = [
            member.number.toString(),
            member.memberNameArabic,
            member.roleArabic,
            member.attendance ? '✓' : '',
            member.attendance ? '✓' : '',
            member.roleEnglish,
            member.memberNameEnglish
          ];
          
          rowData.forEach((cell, idx) => {
            const cellText = doc.splitTextToSize(cell || '', colWidths[idx] - 2);
            cellText.forEach((line: string, lineIdx: number) => {
              doc.text(line, currentX, y + (lineIdx * 4));
            });
            currentX += colWidths[idx];
          });
          
          y += 8;
        });
        
        y += 10;
      }

      // Agenda Description
      if (data.agendaDescription) {
        addText('Agenda Description', 16, true);
        y += 5;
        
        addText('English', 12, true, colors.dark);
        addText(data.agendaDescription.english, 11, false, colors.gray);
        y += 5;
        
        addText('العربية', 12, true, colors.dark);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
        const arabicLines = doc.splitTextToSize(data.agendaDescription.arabic, maxWidth);
        arabicLines.forEach((line: string) => {
          checkPage();
          const textWidth = doc.getTextWidth(line);
          doc.text(line, margin + maxWidth - textWidth, y);
          y += 5;
        });
        doc.setTextColor(0, 0, 0);
        y += 10;
      }

      // Items
      if (data.items?.length > 0) {
        data.items.forEach((item: any) => {
          addText(`Item #${item.itemNumber} - ${item.company}`, 16, true);
          y += 5;
          
          const leftColumnWidth = maxWidth / 2 - 5;
          const rightColumnWidth = maxWidth / 2 - 5;
          const rightColumnStart = margin + maxWidth / 2 + 5;
          
          // English text
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
          const englishIntroLines = doc.splitTextToSize(item.englishText, leftColumnWidth);
          englishIntroLines.forEach((line: string) => {
            checkPage();
            doc.text(line, margin, y);
            y += 5;
          });
          
          y += 3;
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
          checkPage();
          doc.text('Tender Committee Decision:', margin, y);
          y += 5;
          
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
          const englishDecisionLines = doc.splitTextToSize(item.decisionEnglish, leftColumnWidth);
          englishDecisionLines.forEach((line: string) => {
            checkPage();
            doc.text(line, margin, y);
            y += 5;
          });
          
          // Arabic text (right column)
          const arabicStartY = y - (englishDecisionLines.length * 5) - 8 - (englishIntroLines.length * 5);
          let arabicY = arabicStartY;
          
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
          const arabicIntroLines = doc.splitTextToSize(item.arabicText, rightColumnWidth);
          arabicIntroLines.forEach((line: string) => {
            checkPage();
            const textWidth = doc.getTextWidth(line);
            doc.text(line, rightColumnStart + rightColumnWidth - textWidth, arabicY);
            arabicY += 5;
          });
          
          arabicY += 3;
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
          const decisionLabelWidth = doc.getTextWidth('قرار لجنة المناقصات:');
          checkPage();
          doc.text('قرار لجنة المناقصات:', rightColumnStart + rightColumnWidth - decisionLabelWidth, arabicY);
          arabicY += 5;
          
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
          const arabicDecisionLines = doc.splitTextToSize(item.decisionArabic, rightColumnWidth);
          arabicDecisionLines.forEach((line: string) => {
            checkPage();
            const textWidth = doc.getTextWidth(line);
            doc.text(line, rightColumnStart + rightColumnWidth - textWidth, arabicY);
            arabicY += 5;
          });
          
          y = Math.max(y, arabicY);
          y += 15;
        });
      }

      doc.save(`Meeting_Minutes_${this.meetingDetails.meetingId}_${data.recordedDate}.pdf`);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    }
  }

  // ==================== Status Helpers ====================
  isMeetingCompleted(): boolean {
    const status = this.meetingDetails?.status;
    return status === 'Completed' || status === 5 || status === '5';
  }

  // ==================== Color Helpers ====================
  getStatusColor(status: string): BadgeColor {
    switch(status?.toLowerCase()) {
      case 'scheduled': return 'blue';
      case 'waitingforattendance': return 'orange';
      case 'ongoing': return 'purple';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  }

  getAgendaStatusColor(status: string): BadgeColor {
    switch(status?.toLowerCase()) {
      case 'pending': return 'yellow';
      case 'completed': return 'green';
      default: return 'gray';
    }
  }

  getAgendaResponseColor(response: string): BadgeColor {
    switch(response?.toLowerCase()) {
      case 'accepted': return 'green';
      case 'rejected': return 'red';
      case 'pending': return 'yellow';
      default: return 'gray';
    }
  }

  // Get sorted attendance table from meetingMinutesData
  getSortedAttendanceTable(): AttendanceTableMember[] {
    if (!this.meetingDetails?.meetingMinutesData?.attendanceTable || 
        this.meetingDetails.meetingMinutesData.attendanceTable.length === 0) {
      return [];
    }
    
    const roleOrder: { [key: string]: number } = {
      'chairman': 1,
      'vicechairman': 2,
      'vice chairman': 2,
      'deputy chairman': 2,
      'secretary': 3,
      'sec': 3,
      'tender committee secretary': 3,
      'tcm': 4,
      'member': 4,
      'observer': 5
    };
    
    const sorted = [...this.meetingDetails.meetingMinutesData.attendanceTable].sort((a, b) => {
      const roleA = (a.roleEnglish || '').toLowerCase().replace(/\s+/g, '');
      const roleB = (b.roleEnglish || '').toLowerCase().replace(/\s+/g, '');
      
      const orderA = roleOrder[roleA] || 4; // Default to TCM if role not found
      const orderB = roleOrder[roleB] || 4;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same role, sort alphabetically by name
      return (a.memberNameEnglish || '').localeCompare(b.memberNameEnglish || '');
    });
    
    // Update numbers after sorting
    sorted.forEach((member, index) => {
      member.number = index + 1;
    });
    
    return sorted;
  }

  // Get sorted members by role: Chairman -> Vice Chairman -> Secretary -> TCM -> Observer
  getSortedMembers(): Member[] {
    if (!this.meetingDetails?.members || this.meetingDetails.members.length === 0) {
      return [];
    }
    
    // Debug: Log all members before sorting (including observers)
    console.log('All members before sorting (including observers):', this.meetingDetails.members);
    
    const roleOrder: { [key: string]: number } = {
      'chairman': 1,
      'vicechairman': 2,
      'vice chairman': 2,
      'secretary': 3,
      'sec': 3,
      'tcm': 4,
      'member': 4,
      'observer': 5
    };
    
    const sorted = [...this.meetingDetails.members].sort((a, b) => {
      const roleA = (a.role || '').toLowerCase().replace(/\s+/g, '');
      const roleB = (b.role || '').toLowerCase().replace(/\s+/g, '');
      
      // Debug: Log role matching
      if (roleA === 'observer' || roleB === 'observer') {
        console.log('Observer detected - Role A:', roleA, 'Role B:', roleB);
      }
      
      const orderA = roleOrder[roleA] || 4; // Default to TCM if role not found
      const orderB = roleOrder[roleB] || 4;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same role, sort alphabetically by name
      return (a.name || '').localeCompare(b.name || '');
    });
    
    // Debug: Log sorted members (including observers)
    console.log('Sorted members (including observers):', sorted);
    
    return sorted;
  }

  getMemberStatusColor(status: string): BadgeColor {
    switch(status?.toLowerCase()) {
      case 'accepted': return 'green';
      case 'pending': return 'yellow';
      default: return 'gray';
    }
  }

  // ==================== TrackBy Functions ====================
  trackByAgendaItem(index: number, item: AgendaItem): string {
    return item.subject || index.toString();
  }

  trackByMember(index: number, member: Member): string {
    return member.name || index.toString();
  }

  trackByAttachment(index: number, attachment: MeetingAttachment): string {
    return attachment.name || index.toString();
  }

  trackByAttendanceMember(index: number, member: AttendanceTableMember): number {
    return member.number || index;
  }

  trackByMeetingItem(index: number, item: MeetingItem): string {
    return item.itemNumber || index.toString();
  }

  // ==================== Private Methods ====================
  private handleJoinMeetingResult(result: any): void {
    if (result.action === 'accept') {
      console.log('Agenda item accepted with comment:', result.comment);
      // TODO: Implement accept API call
    } else if (result.action === 'reject') {
      console.log('Agenda item rejected with comment:', result.comment);
      // TODO: Implement reject API call
    } else if (result.action === 'back') {
      console.log('Back to agenda list');
    }
  }

  private getMockMeetingDetails(): MeetingDetails {
    return {
      meetingId: 'M-001',
      title: 'Tender Committee Review - Q4 - November 2025',
      dateTime: '2025-11-20T10:00',
      duration: '2 hours',
      location: 'Conference Room A / Teams Link',
      status: 'Scheduled',
      createdBy: 'Sara Al-Naimi',
      createdDate: '2025-11-15',
      members: [
        { name: 'Ahmed Al-Mansouri', role: 'Chairman', initials: 'AA', status: 'Accepted' },
        { name: 'Fatima Al-Khater', role: 'Member', initials: 'FA', status: 'Accepted' },
        { name: 'Mohammed Al-Thani', role: 'Member', initials: 'MA', status: 'Pending' },
        { name: 'Khalid Al-Kuwari', role: 'Member', initials: 'KA', status: 'Accepted' },
        { name: 'Sara Al-Naimi', role: 'Secretary', initials: 'SA', status: 'Accepted' }
      ],
      agendaItems: [
        {
          subject: 'Review and approval of tender evaluation for IT Infrastructure',
          dateTime: '2025-11-20 10:15 AM',
          department: 'IT Department',
          response: 'Accepted',
          attachmentsCount: 3,
          attachments: [
            { name: 'Technical_Evaluation_Report.pdf', size: '2.4 MB', uploadedBy: 'Ahmed Hassan', uploadedDate: '2025-11-18', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment,
            { name: 'Commercial_Evaluation_Summary.xlsx', size: '856 KB', uploadedBy: 'Sarah Al-Mansoori', uploadedDate: '2025-11-18', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment,
            { name: 'Vendor_Comparison_Matrix.pdf', size: '1.2 MB', uploadedBy: 'Mohammed Al-Thani', uploadedDate: '2025-11-19', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment
          ],
          descriptionEnglish: 'Review and approval of tender evaluation for IT Infrastructure. The committee will evaluate vendor submissions and prepare final recommendations.',
          descriptionArabic: 'مراجعة والموافقة على تقييم مناقصة البنية التحتية لتكنولوجيا المعلومات. سيقوم اللجنة بتقييم عروض الموردين وإعداد التوصيات النهائية.',
          requiredFromTC: 'Review and approval of tender evaluation for IT Infrastructure'
        },
        {
          subject: 'Discussion on Network Equipment Procurement',
          dateTime: '2025-11-20 10:45 AM',
          department: 'Network Operations',
          response: 'Accepted',
          attachmentsCount: 2,
          attachments: [
            { name: 'Network_Equipment_Specs.pdf', size: '1.8 MB', uploadedBy: 'Khalid Al-Kuwari', uploadedDate: '2025-11-19', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment,
            { name: 'Vendor_Proposals.xlsx', size: '950 KB', uploadedBy: 'Fatima Al-Khater', uploadedDate: '2025-11-19', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment
          ],
          descriptionEnglish: 'Discussion and review of network equipment procurement proposals. The committee will evaluate vendor submissions, compare technical specifications, pricing, warranty terms, and delivery schedules. Final recommendations will be prepared for approval.',
          descriptionArabic: 'مناقشة ومراجعة مقترحات شراء معدات الشبكة. سيقوم اللجنة بتقييم عروض الموردين ومقارنة المواصفات الفنية والأسعار وشروط الضمان وجداول التسليم. سيتم إعداد التوصيات النهائية للموافقة عليها.',
          requiredFromTC: 'Discussion and review of network equipment procurement proposals'
        },
        {
          subject: 'Award decision for Security Systems tender',
          dateTime: '2025-11-20 11:30 AM',
          department: 'Security Department',
          response: 'Rejected',
          attachmentsCount: 3,
          attachments: [
            { name: 'Security_Systems_Evaluation.pdf', size: '3.1 MB', uploadedBy: 'Sara Al-Naimi', uploadedDate: '2025-11-20', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment,
            { name: 'Bid_Comparison_Matrix.xlsx', size: '1.5 MB', uploadedBy: 'Ahmed Al-Mansouri', uploadedDate: '2025-11-20', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment,
            { name: 'Technical_Assessment.pdf', size: '2.2 MB', uploadedBy: 'Mohammed Al-Thani', uploadedDate: '2025-11-20', url: '#', description: '', date: new Date(), attachmentFor: '', fileContent: '', additionalDetails: '', mimeType: '' } as MeetingAttachment
          ],
          descriptionEnglish: 'Final award decision for Security Systems tender. Review of evaluation results and selection of winning vendor.',
          descriptionArabic: 'قرار منح نهائي لمناقصة أنظمة الأمان. مراجعة نتائج التقييم واختيار المورد الفائز.',
          requiredFromTC: 'Final award decision for Security Systems tender'
        }
      ],
      meetingMinutes: null,
      meetingMinutesData: {
        recordedBy: 'Sara Al-Naimi',
        recordedDate: '2025-11-20',
        summary: 'The Tender Committee convened to review three critical tender evaluations and make award decisions. All committee members were present except Mohammed Al-Thani who joined remotely.',
        attendanceTable: [
          {
            number: 1,
            memberNameArabic: 'المهندس / احمد محمد يوسف',
            memberNameEnglish: 'Eng. Ahmad Mohamed AL-Kuwari',
            roleArabic: 'رئيس اللجنة',
            roleEnglish: 'Chairman',
            attendance: true
          },
          {
            number: 2,
            memberNameArabic: 'السيد / عبد الله الكواري',
            memberNameEnglish: 'Mr. Abdulla Al-Kuwari',
            roleArabic: 'نائب رئيس اللجنة',
            roleEnglish: 'Deputy Chairman',
            attendance: true
          },
          {
            number: 3,
            memberNameArabic: 'المهندس / بدر السادة',
            memberNameEnglish: 'Eng. Bader AlSada',
            roleArabic: 'عضو اللجنة',
            roleEnglish: 'Member',
            attendance: true
          },
          {
            number: 4,
            memberNameArabic: 'السيد / محمد أحمد شيخ',
            memberNameEnglish: 'Mr. Mohammed Ahmed Shaikh',
            roleArabic: 'عضو اللجنة',
            roleEnglish: 'Member',
            attendance: true
          },
          {
            number: 5,
            memberNameArabic: 'المهندس / أحمد أبو النجا',
            memberNameEnglish: 'Eng. Ahmed Abouelnaga',
            roleArabic: 'عضو اللجنة',
            roleEnglish: 'Member',
            attendance: true
          },
          {
            number: 6,
            memberNameArabic: 'السيد / محمد عرفان غني',
            memberNameEnglish: 'Mr. Muhammad Arfan Ghani',
            roleArabic: 'عضو اللجنة',
            roleEnglish: 'Member',
            attendance: true
          },
          {
            number: 7,
            memberNameArabic: 'المهندس / لؤلؤة المهندي',
            memberNameEnglish: 'Eng. Lolwa AlMuhannadi',
            roleArabic: 'إجازة',
            roleEnglish: 'Leave',
            attendance: true
          },
          {
            number: 8,
            memberNameArabic: 'السيد / عبدي الميزرع',
            memberNameEnglish: 'Mr. Eisa Almehaiza',
            roleArabic: 'ممثل ديوان المحاسبة',
            roleEnglish: 'SAB Representative',
            attendance: true
          },
          {
            number: 9,
            memberNameArabic: 'السيد / محمد خالد زنكر',
            memberNameEnglish: 'Mr. Mohamad Khaled Zankar',
            roleArabic: 'أمين سر اللجنة',
            roleEnglish: 'Tender Committee Secretary',
            attendance: true
          }
        ],
        agendaDescription: {
          english: 'Discussion and review of network equipment procurement proposals. The committee will evaluate vendor submissions, compare technical specifications, pricing, warranty terms, and delivery schedules. Final recommendations will be prepared for approval.',
          arabic: 'مناقشة ومراجعة مقترحات شراء معدات الشبكة. ستقوم اللجنة بتقييم مقترحات الموردين ومقارنة المواصفات الفنية والأسعار وشروط الضمان وجداول التسليم. سيتم إعداد التوصيات النهائية للموافقة عليها.'
        },
        items: [
          {
            itemNumber: 'WCO#1842-14.1',
            company: 'Arabilla',
            englishText: 'The Tender Committee reviewed the attached documents.',
            arabicText: 'اطلعت اللجنة على المستندات المرفقة',
            decisionEnglish: 'The Tender Committee unanimously approved the revised change order WCO#1842-14.1 for Arabilla Trading & Contracting to amend the value of the original Purchase Order from 92,882.15 Qatari Riyals to 79,639.23 Qatari riyals, which is equivalent to (-14%).',
            decisionArabic: 'وافقت اللجنة بالإجماع على امر التغيير المعدل رقم WCO#1842-14.1 الشركة أرابيلا التجارة والمقاولات و تعديل قيمة أمر الشراء الأساسي من 92,882.15 ريال قطري إلى 79,639.23 ريال قطري حيث أن نسبة التغيير هي (14%-).',
            acceptedVotes: 5,
            rejectedVotes: 0
          },
          {
            itemNumber: 'WCO#1842-19.1',
            company: 'Arabilla',
            englishText: 'The Tender Committee reviewed the attached documents.',
            arabicText: 'اطلعت اللجنة على المستندات المرفقة',
            decisionEnglish: 'The Tender Committee unanimously approved the revised change order WCO#1842-19.1for Arabilla Trading & Contracting to amend the value of the original Purchase Order from 72,450.77 Qatari Riyals to 70,649.08 Qatari riyals, which is equivalent to (-2.49%).',
            decisionArabic: 'وافقت اللجنة بالإجماع على امر التغيير المعدل رقم WCO#1842-19.1 الشركة أرابيلا التجارة والمقاولات و تعديل قيمة أمر الشراء الأساسي من 72,450.77 ريال قطري إلى 70,649.08 ريال قطري حيث أن نسبة التغيير هي (2.49%-).',
            acceptedVotes: 5,
            rejectedVotes: 0
          }
        ]
      }
    };
  }

  private loadLogoImage(): Promise<{ imgData: string; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = 'assets/img/Bidaya-long.png';
      
      img.onload = () => {
        try {
          const maxWidth = 60;
          const maxHeight = 30;
          let imgWidth = img.width;
          let imgHeight = img.height;
          const aspectRatio = imgWidth / imgHeight;
          
          if (imgWidth > maxWidth) {
            imgWidth = maxWidth;
            imgHeight = imgWidth / aspectRatio;
          }
          
          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * aspectRatio;
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const imgData = canvas.toDataURL('image/png');
            resolve({ imgData, width: imgWidth, height: imgHeight });
          } else {
            reject(new Error('Failed to get canvas context'));
          }
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load logo image'));
      };
    });
  }
}
